// src/dateUtils.ts
import { format, parseISO, isToday, isSameDay, differenceInDays, addDays, subDays, startOfDay, endOfDay, getMonth, getYear } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
function formatDate(date, formatStr = "MMM d, yyyy") {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return format(dateObj, formatStr);
}
function formatDateInTimezone(date, timezone, formatStr = "MMM d, yyyy") {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return formatInTimeZone(dateObj, timezone, formatStr);
}
function getCurrentDateInTimezone(timezone) {
  return toZonedTime(/* @__PURE__ */ new Date(), timezone);
}
function isTodayInTimezone(date, timezone) {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  const zonedDate = toZonedTime(dateObj, timezone);
  const zonedNow = toZonedTime(/* @__PURE__ */ new Date(), timezone);
  return isSameDay(zonedDate, zonedNow);
}
function getOnThisDayDate(targetYear, referenceDate = /* @__PURE__ */ new Date()) {
  const month = getMonth(referenceDate);
  const targetDate = new Date(targetYear, month, referenceDate.getDate());
  return format(targetDate, "yyyy-MM-dd");
}
function getOnThisDayDates(yearsBack = 30, referenceDate = /* @__PURE__ */ new Date()) {
  const currentYear = getYear(referenceDate);
  const dates = [];
  for (let i = 1; i <= yearsBack; i++) {
    dates.push(getOnThisDayDate(currentYear - i, referenceDate));
  }
  return dates;
}
function calculateStreakDays(dates) {
  if (dates.length === 0) return 0;
  const sortedDates = [...dates].sort((a, b) => b.getTime() - a.getTime());
  let streak = 1;
  for (let i = 1; i < sortedDates.length; i++) {
    const diff = differenceInDays(sortedDates[i - 1], sortedDates[i]);
    if (diff === 1) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
}
function getSouthernHemisphereSeason(date = /* @__PURE__ */ new Date()) {
  const month = getMonth(date);
  if (month >= 11 || month <= 1) return "summer";
  if (month >= 2 && month <= 4) return "autumn";
  if (month >= 5 && month <= 7) return "winter";
  return "spring";
}
function getNorthernHemisphereSeason(date = /* @__PURE__ */ new Date()) {
  const month = getMonth(date);
  if (month >= 5 && month <= 7) return "summer";
  if (month >= 8 && month <= 10) return "autumn";
  if (month >= 11 || month <= 1) return "winter";
  return "spring";
}
function formatRelativeTime(date) {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  const now = /* @__PURE__ */ new Date();
  const diffDays = differenceInDays(now, dateObj);
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
function getDayOfYear(date = /* @__PURE__ */ new Date()) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  const oneDay = 1e3 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}
function isLeapYear(year) {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
}
function daysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

// src/formatters.ts
function celsiusToFahrenheit(celsius) {
  return Math.round(celsius * 9 / 5 + 32);
}
function fahrenheitToCelsius(fahrenheit) {
  return Math.round((fahrenheit - 32) * 5 / 9);
}
function formatTemperature(celsius, unit = "celsius", includeUnit = true) {
  const value = unit === "fahrenheit" ? celsiusToFahrenheit(celsius) : Math.round(celsius);
  const unitSymbol = unit === "fahrenheit" ? "F" : "C";
  return includeUnit ? `${value}\xB0${unitSymbol}` : `${value}\xB0`;
}
function formatTemperatureRange(min, max, unit = "celsius") {
  const minFormatted = formatTemperature(min, unit, false);
  const maxFormatted = formatTemperature(max, unit);
  return `${minFormatted} - ${maxFormatted}`;
}
function formatPrecipitation(mm) {
  if (mm === 0) return "No rain";
  if (mm < 0.1) return "Trace";
  if (mm < 1) return `${mm.toFixed(1)}mm`;
  return `${Math.round(mm)}mm`;
}
function formatWindSpeed(kmh) {
  if (kmh < 1) return "Calm";
  if (kmh < 20) return `${Math.round(kmh)} km/h (light)`;
  if (kmh < 40) return `${Math.round(kmh)} km/h (moderate)`;
  if (kmh < 60) return `${Math.round(kmh)} km/h (strong)`;
  return `${Math.round(kmh)} km/h (very strong)`;
}
function formatUVIndex(uv) {
  if (uv < 3) return `${uv.toFixed(1)} (Low)`;
  if (uv < 6) return `${uv.toFixed(1)} (Moderate)`;
  if (uv < 8) return `${uv.toFixed(1)} (High)`;
  if (uv < 11) return `${uv.toFixed(1)} (Very High)`;
  return `${uv.toFixed(1)} (Extreme)`;
}
function formatHumidity(percent) {
  return `${Math.round(percent)}%`;
}
function formatWeatherCondition(condition) {
  const conditions = {
    clear: "Clear",
    partly_cloudy: "Partly Cloudy",
    cloudy: "Cloudy",
    overcast: "Overcast",
    fog: "Foggy",
    drizzle: "Drizzle",
    rain: "Rain",
    heavy_rain: "Heavy Rain",
    thunderstorm: "Thunderstorm",
    snow: "Snow",
    sleet: "Sleet",
    hail: "Hail"
  };
  return conditions[condition] || condition;
}
function formatCloudType(cloudType) {
  const types = {
    cumulus: "Cumulus",
    stratus: "Stratus",
    cirrus: "Cirrus",
    cumulonimbus: "Cumulonimbus",
    nimbostratus: "Nimbostratus",
    altocumulus: "Altocumulus",
    altostratus: "Altostratus",
    stratocumulus: "Stratocumulus",
    cirrocumulus: "Cirrocumulus",
    cirrostratus: "Cirrostratus"
  };
  return types[cloudType] || cloudType;
}
function formatStoryType(storyType) {
  const types = {
    on_this_day: "On This Day",
    record: "Record",
    streak: "Streak",
    comparison: "Comparison",
    astronomical: "Astronomical",
    this_day_in_history: "This Day in History",
    similarity: "Weather Twin",
    monthly_milestone: "Monthly Milestone",
    local_flavor: "Local Flavor",
    month_fact: "Monthly Insider",
    weather_trivia: "Trivia",
    humor: "Lighter Side"
  };
  return types[storyType] || storyType;
}
function formatNumber(num) {
  if (num < 1e3) return num.toString();
  if (num < 1e6) return `${(num / 1e3).toFixed(1)}K`;
  return `${(num / 1e6).toFixed(1)}M`;
}
function formatPercentage(value, decimals = 0) {
  return `${value.toFixed(decimals)}%`;
}
function formatOrdinal(num) {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}
function formatTemperatureChange(change, unit = "celsius") {
  const value = unit === "fahrenheit" ? celsiusToFahrenheit(change) - 32 : change;
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}\xB0`;
}
function getSuperlative(type) {
  const superlatives = {
    hot: "hottest",
    cold: "coldest",
    wet: "wettest",
    dry: "driest",
    windy: "windiest"
  };
  return superlatives[type] || type;
}
function getComparative(type) {
  const comparatives = {
    hot: "hotter",
    cold: "colder",
    wet: "wetter",
    dry: "drier",
    windy: "windier"
  };
  return comparatives[type] || type;
}

// src/validators.ts
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
function isValidPassword(password) {
  if (password.length < 8) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  return true;
}
function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 2) return "weak";
  if (score <= 4) return "medium";
  return "strong";
}
function isValidLatitude(lat) {
  return lat >= -90 && lat <= 90;
}
function isValidLongitude(lng) {
  return lng >= -180 && lng <= 180;
}
function isValidCoordinates(lat, lng) {
  return isValidLatitude(lat) && isValidLongitude(lng);
}
function isValidDateString(dateStr) {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}
function isValidTemperature(temp) {
  return temp >= -100 && temp <= 60;
}
function isValidPrecipitation(mm) {
  return mm >= 0 && mm <= 1e3;
}
function isValidWindSpeed(kmh) {
  return kmh >= 0 && kmh <= 500;
}
function isValidUVIndex(uv) {
  return uv >= 0 && uv <= 20;
}
function isValidHumidity(percent) {
  return percent >= 0 && percent <= 100;
}
function isValidBomStationId(stationId) {
  return /^\d{6}$/.test(stationId);
}
function isValidApiKey(apiKey) {
  return /^[A-Za-z0-9_-]{20,}$/.test(apiKey);
}
function sanitizeInput(input) {
  return input.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#x27;").replace(/\//g, "&#x2F;");
}
function isValidDisplayName(name) {
  return /^[A-Za-z0-9\s\-_.]{2,50}$/.test(name);
}

// src/constants.ts
var API_VERSION = "v1";
var DEFAULT_PAGE_SIZE = 20;
var MAX_PAGE_SIZE = 100;
var ACCESS_TOKEN_EXPIRY = "15m";
var REFRESH_TOKEN_EXPIRY = "7d";
var PASSWORD_MIN_LENGTH = 8;
var DAILY_STORY_COUNT = 16;
var MAX_STORY_LENGTH = 280;
var STORY_CACHE_TTL = 6 * 60 * 60;
var WEATHER_CACHE_TTL = 60 * 60;
var FORECAST_CACHE_TTL = 24 * 60 * 60;
var HISTORICAL_YEARS_DEFAULT = 30;
var Z_SCORE_NOTABLE = 2;
var Z_SCORE_EXTREME = 3;
var PERCENTILE_NOTABLE_HIGH = 95;
var PERCENTILE_NOTABLE_LOW = 5;
var STREAK_MIN_DAYS = 3;
var POINTS_SHARE_STORY = 10;
var POINTS_UPLOAD_CLOUD = 25;
var POINTS_DAILY_LOGIN = 5;
var POINTS_REFERRAL = 100;
var LEVEL_THRESHOLDS = [0, 100, 500, 1e3, 2500, 5e3, 1e4];
var PRICE_BASIC_MONTHLY = 299;
var PRICE_BASIC_YEARLY = 2999;
var RATE_LIMIT_PUBLIC = 200;
var RATE_LIMIT_AUTHENTICATED = 1e3;
var RATE_LIMIT_PREMIUM = 4e3;
var RATE_LIMIT_API_BASIC = 200;
var MAX_IMAGE_SIZE = 10 * 1024 * 1024;
var ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
var IMAGE_COMPRESSION_QUALITY = 0.8;
var AUSTRALIAN_MAJOR_CITIES = [
  { name: "Sydney", state: "NSW", lat: -33.8688, lng: 151.2093, timezone: "Australia/Sydney", bomStationId: "r3gx2f" },
  { name: "Melbourne", state: "VIC", lat: -37.8136, lng: 144.9631, timezone: "Australia/Melbourne", bomStationId: "r1r143" },
  { name: "Brisbane", state: "QLD", lat: -27.4698, lng: 153.0251, timezone: "Australia/Brisbane", bomStationId: "r7hf4g" },
  { name: "Perth", state: "WA", lat: -31.9505, lng: 115.8605, timezone: "Australia/Perth", bomStationId: "qd66hq" },
  { name: "Adelaide", state: "SA", lat: -34.9285, lng: 138.6007, timezone: "Australia/Adelaide", bomStationId: "r1f9p0" },
  { name: "Hobart", state: "TAS", lat: -42.8821, lng: 147.3272, timezone: "Australia/Hobart", bomStationId: "r28g3q" },
  { name: "Darwin", state: "NT", lat: -12.4634, lng: 130.8456, timezone: "Australia/Darwin", bomStationId: "qfktf1" },
  { name: "Canberra", state: "ACT", lat: -35.2809, lng: 149.13, timezone: "Australia/Sydney", bomStationId: "r3dp1g" },
  { name: "Gold Coast", state: "QLD", lat: -28.0167, lng: 153.4, timezone: "Australia/Brisbane", bomStationId: "r7h4p9" },
  { name: "Newcastle", state: "NSW", lat: -32.9283, lng: 151.7817, timezone: "Australia/Sydney", bomStationId: "r3hv5c" }
];
var AUSTRALIAN_REGIONAL_CITIES = [
  { name: "Cairns", state: "QLD", lat: -16.9186, lng: 145.7781, timezone: "Australia/Brisbane", bomStationId: "rn1x3e" },
  { name: "Townsville", state: "QLD", lat: -19.259, lng: 146.8169, timezone: "Australia/Brisbane", bomStationId: "rkwf4s" },
  { name: "Sunshine Coast", state: "QLD", lat: -26.65, lng: 153.0667, timezone: "Australia/Brisbane", bomStationId: "r7hf1p" },
  { name: "Wollongong", state: "NSW", lat: -34.4278, lng: 150.8931, timezone: "Australia/Sydney", bomStationId: "r3g9yt" },
  { name: "Geelong", state: "VIC", lat: -38.1499, lng: 144.3617, timezone: "Australia/Melbourne", bomStationId: "r1qjpc" },
  { name: "Ballarat", state: "VIC", lat: -37.5622, lng: 143.8503, timezone: "Australia/Melbourne", bomStationId: "r1p9un" },
  { name: "Bendigo", state: "VIC", lat: -36.757, lng: 144.2794, timezone: "Australia/Melbourne", bomStationId: "r1s4k5" },
  { name: "Launceston", state: "TAS", lat: -41.4332, lng: 147.1441, timezone: "Australia/Hobart", bomStationId: "r23w85" },
  { name: "Albury", state: "NSW", lat: -36.0737, lng: 146.9135, timezone: "Australia/Sydney", bomStationId: "r3d0j3" },
  { name: "Toowoomba", state: "QLD", lat: -27.5598, lng: 151.9507, timezone: "Australia/Brisbane", bomStationId: "r7h2nm" },
  { name: "Alice Springs", state: "NT", lat: -23.698, lng: 133.8807, timezone: "Australia/Darwin", bomStationId: "qgr5g9" },
  { name: "Broome", state: "WA", lat: -17.9614, lng: 122.2359, timezone: "Australia/Perth", bomStationId: "qhxfb3" },
  { name: "Mackay", state: "QLD", lat: -21.1411, lng: 149.1861, timezone: "Australia/Brisbane", bomStationId: "rk6z5p" },
  { name: "Rockhampton", state: "QLD", lat: -23.3791, lng: 150.51, timezone: "Australia/Brisbane", bomStationId: "rhv31r" },
  { name: "Bunbury", state: "WA", lat: -33.3271, lng: 115.6414, timezone: "Australia/Perth", bomStationId: "qd4fz4" }
];
var INTERNATIONAL_CITIES = [
  // New Zealand
  { name: "Auckland", country: "New Zealand", lat: -36.8485, lng: 174.7633, timezone: "Pacific/Auckland" },
  { name: "Wellington", country: "New Zealand", lat: -41.2866, lng: 174.7756, timezone: "Pacific/Auckland" },
  { name: "Christchurch", country: "New Zealand", lat: -43.5321, lng: 172.6362, timezone: "Pacific/Auckland" },
  // Asia-Pacific
  { name: "Singapore", country: "Singapore", lat: 1.3521, lng: 103.8198, timezone: "Asia/Singapore" },
  { name: "Tokyo", country: "Japan", lat: 35.6762, lng: 139.6503, timezone: "Asia/Tokyo" },
  { name: "Hong Kong", country: "Hong Kong", lat: 22.3193, lng: 114.1694, timezone: "Asia/Hong_Kong" },
  { name: "Seoul", country: "South Korea", lat: 37.5665, lng: 126.978, timezone: "Asia/Seoul" },
  { name: "Bangkok", country: "Thailand", lat: 13.7563, lng: 100.5018, timezone: "Asia/Bangkok" },
  { name: "Bali", country: "Indonesia", lat: -8.3405, lng: 115.092, timezone: "Asia/Makassar" },
  { name: "Kuala Lumpur", country: "Malaysia", lat: 3.139, lng: 101.6869, timezone: "Asia/Kuala_Lumpur" },
  // Europe
  { name: "London", country: "United Kingdom", lat: 51.5074, lng: -0.1278, timezone: "Europe/London" },
  { name: "Paris", country: "France", lat: 48.8566, lng: 2.3522, timezone: "Europe/Paris" },
  { name: "Amsterdam", country: "Netherlands", lat: 52.3676, lng: 4.9041, timezone: "Europe/Amsterdam" },
  { name: "Berlin", country: "Germany", lat: 52.52, lng: 13.405, timezone: "Europe/Berlin" },
  { name: "Rome", country: "Italy", lat: 41.9028, lng: 12.4964, timezone: "Europe/Rome" },
  { name: "Barcelona", country: "Spain", lat: 41.3851, lng: 2.1734, timezone: "Europe/Madrid" },
  // Americas
  { name: "New York", country: "United States", lat: 40.7128, lng: -74.006, timezone: "America/New_York" },
  { name: "Los Angeles", country: "United States", lat: 34.0522, lng: -118.2437, timezone: "America/Los_Angeles" },
  { name: "San Francisco", country: "United States", lat: 37.7749, lng: -122.4194, timezone: "America/Los_Angeles" },
  { name: "Vancouver", country: "Canada", lat: 49.2827, lng: -123.1207, timezone: "America/Vancouver" },
  { name: "Toronto", country: "Canada", lat: 43.6532, lng: -79.3832, timezone: "America/Toronto" },
  // Pacific Islands
  { name: "Fiji", country: "Fiji", lat: -17.7134, lng: 178.065, timezone: "Pacific/Fiji" },
  { name: "Honolulu", country: "United States", lat: 21.3069, lng: -157.8583, timezone: "Pacific/Honolulu" }
];
var ALL_CITIES = [
  ...AUSTRALIAN_MAJOR_CITIES.map((c) => ({ ...c, country: "Australia" })),
  ...AUSTRALIAN_REGIONAL_CITIES.map((c) => ({ ...c, country: "Australia" })),
  ...INTERNATIONAL_CITIES
];
var CLIMATE_SCENARIOS = [
  { id: "SSP1-2.6", name: "Low Emissions", description: "Sustainability pathway" },
  { id: "SSP2-4.5", name: "Medium Emissions", description: "Middle of the road" },
  { id: "SSP5-8.5", name: "High Emissions", description: "Fossil-fueled development" }
];
var PROJECTION_PERIODS = ["2030", "2050", "2070", "2090"];
var MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
var DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];
var SEASONS_SOUTHERN = {
  summer: { months: [12, 1, 2], name: "Summer" },
  autumn: { months: [3, 4, 5], name: "Autumn" },
  winter: { months: [6, 7, 8], name: "Winter" },
  spring: { months: [9, 10, 11], name: "Spring" }
};
var FUN_REFERENCES = {
  hot: [
    "perfect for a cold one",
    "beach weather for sure",
    "time to crank up the aircon",
    "sunscreen is your best friend today"
  ],
  cold: [
    "soup weather",
    "time for a cuppa",
    "blanket day",
    "perfect excuse to stay in"
  ],
  rainy: [
    "great day for a movie marathon",
    "don't forget your brolly",
    "the ducks are happy",
    "perfect reading weather"
  ],
  mild: [
    "Goldilocks weather - just right",
    "perfect for a walk",
    "window-open weather",
    "the kind of day you wish you could bottle"
  ]
};
var MONTH_FACTS = {
  0: [
    // January
    "January is peak pool season \u2014 if you haven't already, it's time to find your local aquatic centre.",
    "The UV index in January is brutal. Slip, slop, slap isn't optional \u2014 it's survival.",
    "Planning a January BBQ? Start early. By 2pm the heat will chase everyone indoors.",
    "January is the month for early-morning beach runs before the sand gets hot enough to cook an egg.",
    "Australia Day falls in January \u2014 plan your outdoor celebrations with shade and plenty of cold drinks.",
    "January has the longest daylight hours. Make the most of those late sunsets with evening picnics.",
    "Bushfire risk peaks in January. Check the fire danger rating before heading into the bush.",
    "January tip: schedule outdoor exercise before 7am or after 6pm. Your body will thank you."
  ],
  1: [
    // February
    "February is the best month to hold an outdoor party \u2014 warm evenings, long days, and summer energy. Just make sure you're ready to cool everyone down.",
    "If you're planning a party, consider pushing it to March \u2014 it's cooler but still unlikely to rain.",
    "February is summer's last hurrah. Get those beach trips in before the water starts cooling.",
    "School's back in February \u2014 weekday beaches are blissfully empty. Take a sneaky day off.",
    "February nights are perfect for outdoor dining. Warm enough for a t-shirt, cool enough to enjoy your food.",
    "Tropical cyclone season peaks in February up north. If you're in QLD or NT, keep an eye on the radar.",
    "February is grape harvest season in Aussie wine regions. Book a cellar door tour while the vines are heavy.",
    "The secret to February fitness: swim. It's the one month where the ocean, the pool, and the weather all agree."
  ],
  2: [
    // March
    "March is the Goldilocks month \u2014 warm days without the scorching heat. Perfect for almost anything outdoors.",
    "Planning an outdoor event? March is your safest bet. Comfortable temps and rain is still unlikely in most of the south.",
    "March marks the start of autumn, but don't be fooled \u2014 it still feels like summer in most of Australia.",
    "The autumn equinox in March means days and nights are roughly equal. Enjoy the balance while it lasts.",
    "March is peak hiking month. The trails are cooler, the flies are fewer, and the views are golden.",
    "Easter often falls in March \u2014 perfect camping weather if you can snag a spot.",
    "March evenings cool down just enough for a bonfire. Stock up on marshmallows.",
    "Food festival season kicks off in March. Melbourne, Adelaide, and the Hunter Valley come alive."
  ],
  3: [
    // April
    "April is ideal camping weather \u2014 warm days, cool nights, and minimal bugs. Book that campsite now.",
    "The autumn leaves start turning in April across the cooler regions. Road trip to the Dandenongs or Blue Mountains.",
    "April is the month where you can finally sleep without aircon. Your electricity bill will thank you.",
    "AFL season is in full swing by April. Perfect weather for a day at the footy.",
    "April is the last reliable month for beach swims in southern states. Make it count.",
    "Planning outdoor exercise? April is the personal trainer's favourite month \u2014 not too hot, not too cold.",
    "April showers? Not in most of Australia. Save the umbrella for May.",
    "ANZAC Day in April often brings crisp, clear dawn services. Rug up for the 5am start."
  ],
  4: [
    // May
    "May is when the winter wardrobe comes out. Time to remember where you stashed those jumpers.",
    "Soup and slow-cooker season officially begins in May. Dig out those comfort food recipes.",
    "Vivid Sydney typically starts in May \u2014 rug up and enjoy the light displays along the harbour.",
    "May is a great month for road trips \u2014 mild conditions, fewer crowds, and off-peak prices.",
    "First frost risk arrives in May for southern areas. Protect those plants and warm up the car early.",
    "May is perfect for visiting the tropical north \u2014 the wet season is over and humidity drops.",
    "Footy season heats up in May even as the weather cools down. Pick your tipping comp wisely.",
    "May mornings are crisp but sunny \u2014 ideal for a brisk walk with a takeaway coffee."
  ],
  5: [
    // June
    "June brings the shortest days \u2014 perfect excuse for movie nights and early dinners.",
    "Ski season opens in June! Hit the slopes at Thredbo, Perisher, Falls Creek, or Mt Buller.",
    "June is peak whale watching season on the east coast. Humpbacks are heading north.",
    "Warm comfort food weather has arrived. Think pies, roasts, and anything with gravy.",
    "June tip: the tropical north is in its dry season. Perfect time for a Darwin or Cairns escape.",
    "Cozy pub weather is here. Find one with a fireplace and settle in for the evening.",
    "June sunsets come early but they're often spectacular \u2014 winter skies have the best colours.",
    "School holidays in June mean ski trips, but also great deals for non-snow destinations."
  ],
  6: [
    // July
    "July is peak ski season. If you've been putting off that snow trip, now's the time.",
    "July is the best month for whale watching \u2014 humpbacks are in full migration along the coast.",
    "Splendour in the Grass typically falls in July. Pack for mud, hope for sun.",
    "July mornings are frosty but often give way to brilliant sunny days. Layer up and get outside.",
    "The tropical dry season makes July perfect for visiting Kakadu, the Kimberley, or the Daintree.",
    "July school holidays = family travel time. Book early or enjoy the quiet of staying home.",
    "Cold July nights are made for red wine, cheese boards, and good company.",
    "July tip: mid-winter is halfway to summer. The days are already getting longer."
  ],
  7: [
    // August
    "Wildflower season begins in Western Australia in August. The outback blooms in spectacular colour.",
    "August is the light at the end of winter \u2014 spring is just around the corner.",
    "Good month for wine region visits. Winter cellar door prices and cozy tastings by the fire.",
    "Wattle Day on September 1 is approaching \u2014 August is when the golden blooms start appearing.",
    "Daylight hours are increasing noticeably in August. You can feel the shift.",
    "Last of the cool-weather bargain travel in August. Grab those off-peak deals before spring hits.",
    "August is a great month for indoor markets, galleries, and cultural events.",
    "The ski season is winding down \u2014 catch the last of the snow before it melts."
  ],
  8: [
    // September
    "AFL and NRL finals fever hits in September. Plan your grand final viewing party.",
    "Spring flowers are blooming everywhere. Floriade in Canberra is a must-see.",
    "September is the personal trainer's dream \u2014 not too hot, not too cold, no excuses.",
    "Garden planting season is here. Get those seedlings in before the heat arrives.",
    "The spring equinox means days are getting longer fast. Outdoor dining is back on the menu.",
    "September is perfect for coastal walks \u2014 mild temps and migrating whales offshore.",
    "Cricket season is just weeks away. Dust off the bat and get some practice in.",
    "Melbourne Cup carnival season starts building. Time to plan your outfit."
  ],
  9: [
    // October
    "October is the ultimate BBQ month \u2014 warm enough to enjoy, cool enough to stand near the grill.",
    "Beach season is warming up. The brave ones are already swimming; the smart ones are waiting till November.",
    "Jacaranda season turns Sydney streets purple in October. It's nature's Instagram filter.",
    "The Melbourne Cup build-up begins in October. Spring racing carnival is in full swing.",
    "October camping is chef's kiss \u2014 perfect temperatures, long days, and wildflowers still blooming.",
    "Spring storms can be dramatic in October. Keep an eye on the sky for afternoon thunderstorms.",
    "October is great for outdoor exercise. No excuses \u2014 the weather is literally perfect.",
    "Daylight saving kicks in around October. Enjoy that extra hour of evening sunshine."
  ],
  10: [
    // November
    "Beach season is officially on in November. The water is warming up and the days are long.",
    "November is warm enough for outdoor dining every night. Book that rooftop restaurant.",
    "Schoolies and end-of-year events ramp up in November. Plan ahead to avoid the crowds.",
    "Christmas party planning weather \u2014 November is when you should lock in that outdoor venue.",
    "Cricket season starts in November. Backyard or stadium, it's time to play.",
    "November evenings are comfortable and long. Perfect for after-work walks or bike rides.",
    "The jacarandas are still going strong and the frangipani is kicking in. November smells amazing.",
    "November tip: apply sunscreen daily from now until March. The UV is ramping up fast."
  ],
  11: [
    // December
    "December is peak outdoor entertaining season. Christmas, NYE, and everything in between.",
    "The longest days of the year arrive in December. Sunset after 8pm? Yes please.",
    "December beach tip: go early. By 10am the car park is full and the sand is taken.",
    "Holiday mode is activated. Whether you're working or not, December energy is infectious.",
    "Test cricket season is here. Nothing says Aussie summer like cricket on the telly and a cold drink.",
    "December storms can be fierce \u2014 keep an eye on afternoon thunderstorms, especially inland.",
    "Christmas Day weather is always the big question. Will it be a scorcher or a rare cool change?",
    "December tip: hydrate like it's your job. Aussie summers don't mess around."
  ]
};
var WEATHER_TRIVIA = [
  "A single thunderstorm can release more energy than an atomic bomb.",
  "Lightning strikes the Earth about 100 times every second.",
  "The fastest raindrop falls at about 32 km/h.",
  "Antarctica is technically a desert \u2014 it gets less than 200mm of precipitation per year.",
  "Mildura once recorded 50.7\xB0C in 1906 \u2014 though unofficial, it's one of the hottest ever in Australia.",
  "Clouds can weigh over 500,000 kg \u2014 they float because the droplets are spread across a huge volume.",
  'The smell after rain is called "petrichor" \u2014 it comes from oils released by plants and bacteria in the soil.',
  "Australia's wettest town is Tully in Far North Queensland, averaging over 4,000mm of rain per year.",
  "Hail the size of softballs fell in Sydney in 1999, causing $1.7 billion in damage.",
  "The hottest temperature ever recorded on Earth was 56.7\xB0C in Death Valley, California in 1913.",
  "Sea breezes can drop coastal temperatures by 10\xB0C in under an hour.",
  "Fog is just a cloud that touches the ground.",
  "A hurricane releases the energy equivalent of 10 atomic bombs per second.",
  "Snow has fallen in every Australian state \u2014 even Queensland.",
  "The coldest temperature in Australia was -23\xB0C at Charlotte Pass, NSW in 1994.",
  "Red sunsets are caused by particles in the atmosphere scattering shorter wavelengths of light.",
  "Ball lightning is so rare that scientists still debate whether it's real.",
  "Dust storms in Australia can carry soil thousands of kilometres \u2014 red dust from the outback has reached New Zealand.",
  "The Bureau of Meteorology was established in 1906 and is one of the oldest weather services in the world.",
  "Rainbows are actually full circles \u2014 you only see a half-arc because the ground gets in the way.",
  "Wind doesn't have a fixed speed \u2014 gusts can be 50% stronger than the average wind speed.",
  "The UV index in Australia regularly exceeds 11 \u2014 extreme levels that can burn unprotected skin in minutes.",
  "Frost forms when surfaces cool below 0\xB0C, even if the air temperature is above freezing.",
  "Cumulonimbus clouds can tower up to 20km high \u2014 taller than most commercial flights."
];
var HUMOR_PROMPTS = [
  "The weather today is giving main character energy.",
  "Mother Nature clearly didn't check the forecast before committing to this.",
  `Today's weather is brought to you by "just check the app, mate."`,
  "The sky is doing that thing again where it can't make up its mind.",
  "If the weather were a movie, today would be the sequel nobody asked for.",
  "The barometric pressure called \u2014 it wants its drama back.",
  "Today's forecast: 100% chance of weather happening.",
  "The clouds are having a meeting and we're not invited.",
  `Wind chill: the weather's way of saying "you thought you were dressed warm enough."`,
  "The sun showed up to work today. Full marks for attendance.",
  "Rain or shine, the weather always wins the argument.",
  "Today's humidity level: swimming through air."
];
var ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  RATE_LIMITED: "RATE_LIMITED",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  AI_SERVICE_ERROR: "AI_SERVICE_ERROR",
  WEATHER_SERVICE_ERROR: "WEATHER_SERVICE_ERROR",
  PAYMENT_ERROR: "PAYMENT_ERROR"
};

// src/locationProfiles.ts
var LOCATION_PROFILES = {
  // === MAJOR CITIES ===
  sydney: {
    id: "sydney",
    name: "Sydney",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: true,
      // Blue Mountains nearby
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Warm to hot and humid",
      typicalWinter: "Mild and cool",
      rainyPeriod: "Autumn and early winter"
    },
    context: {
      knownFor: ["Harbour", "beaches", "Opera House", "Harbour Bridge"],
      commonActivities: ["surfing", "beach walks", "harbour ferries", "coastal walks"],
      localLandmarks: ["Sydney Opera House", "Harbour Bridge", "Bondi Beach", "Blue Mountains"],
      culturalNotes: ["Busy city lifestyle", "Strong cafe culture", "Rugby league heartland"]
    },
    suggestions: {
      hotDay: ["Perfect for Bondi", "Hit the beach", "Jump in the harbour pool", "Time for a swim"],
      coldDay: ["Cosy cafe time", "Museum day", "Perfect for the pub"],
      rainyDay: ["Gallery weather", "Shopping arcade day", "Movie marathon time"],
      mildDay: ["Harbour walk weather", "Perfect for the coastal walk", "Outdoor brunch conditions"]
    },
    avoid: []
  },
  melbourne: {
    id: "melbourne",
    name: "Melbourne",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: true,
      // Dandenongs, ski fields 3hrs away
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm with occasional heatwaves",
      typicalWinter: "Cool to cold and wet"
    },
    context: {
      knownFor: ["Four seasons in one day", "coffee", "laneways", "AFL", "arts"],
      commonActivities: ["cafe hopping", "laneway exploring", "watching the footy", "beach trips"],
      localLandmarks: ["MCG", "Federation Square", "Flinders Street Station", "Great Ocean Road"],
      culturalNotes: ["Coffee obsessed", "AFL heartland", "Arts and culture hub", "Fashion forward"]
    },
    suggestions: {
      hotDay: ["Beach day at St Kilda", "Time for a cold brew", "Perfect rooftop bar weather"],
      coldDay: ["Cosy coffee shop time", "Perfect for a warming bowl of pho", "Rug up for the footy"],
      rainyDay: ["Classic Melbourne", "Laneway bar weather", "Gallery hopping time", "Perfect excuse for another coffee"],
      mildDay: ["Goldilocks weather for Melbourne", "Perfect for the Tan", "Outdoor dining conditions"]
    },
    avoid: []
  },
  brisbane: {
    id: "brisbane",
    name: "Brisbane",
    geography: {
      terrain: ["coastal", "river_valley"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid with storms",
      typicalWinter: "Mild and dry",
      rainyPeriod: "December to March"
    },
    context: {
      knownFor: ["River City", "South Bank", "sunshine", "laid-back vibe"],
      commonActivities: ["river walks", "outdoor dining", "beach trips to Gold Coast"],
      localLandmarks: ["South Bank", "Story Bridge", "Mt Coot-tha", "Roma Street Parkland"],
      culturalNotes: ["Relaxed lifestyle", "Outdoor culture", "Rugby league territory"]
    },
    suggestions: {
      hotDay: ["South Bank lagoon time", "Stay near the aircon", "Storm watch later"],
      coldDay: ["Brisbane cold? Enjoy the mild winter", "Most cities would call this pleasant"],
      rainyDay: ["Afternoon storm rolling in", "Classic summer storm", "Gallery time"],
      mildDay: ["Perfect Brisbane weather", "River walk conditions", "Al fresco dining"]
    },
    avoid: []
  },
  perth: {
    id: "perth",
    name: "Perth",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Mild and wet",
      rainyPeriod: "May to August"
    },
    context: {
      knownFor: ["Beaches", "isolation", "sunsets", "mining boom"],
      commonActivities: ["beach days", "sunset watching", "river activities", "wine region visits"],
      localLandmarks: ["Kings Park", "Cottesloe Beach", "Rottnest Island", "Swan River"],
      culturalNotes: ["Laid-back beach culture", "Mining industry influence", "Best sunsets in Australia"]
    },
    suggestions: {
      hotDay: ["Beach day at Cottesloe", "Cool off in the Swan", "Rottnest quokka weather"],
      coldDay: ["Perth winter - still pretty nice", "Wine region day trip weather"],
      rainyDay: ["Rare Perth rain", "Museum or gallery time"],
      mildDay: ["Perfect Perth conditions", "Kings Park picnic weather", "Sunset viewing time"]
    },
    avoid: []
  },
  adelaide: {
    id: "adelaide",
    name: "Adelaide",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: true,
      // Adelaide Hills
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Mild and wet"
    },
    context: {
      knownFor: ["Wine regions", "festivals", "churches", "parklands"],
      commonActivities: ["wine tasting", "beach visits", "hills drives", "festival going"],
      localLandmarks: ["Adelaide Oval", "Barossa Valley", "Central Market", "Glenelg Beach"],
      culturalNotes: ["Festival state", "Wine culture", "Relaxed pace", "Church city history"]
    },
    suggestions: {
      hotDay: ["Glenelg beach time", "Cellar door with aircon", "Hills escape for cooler temps"],
      coldDay: ["Central Market browsing", "Cosy winery lunch", "Perfect Barossa weather"],
      rainyDay: ["Market day", "Wine tasting weather", "Gallery time"],
      mildDay: ["Perfect Adelaide conditions", "Parklands picnic", "Outdoor dining on Rundle"]
    },
    avoid: []
  },
  hobart: {
    id: "hobart",
    name: "Hobart",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      // Mt Wellington
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild and pleasant",
      typicalWinter: "Cold with occasional snow on kunanyi"
    },
    context: {
      knownFor: ["MONA", "kunanyi/Mt Wellington", "Salamanca", "pristine nature"],
      commonActivities: ["hiking", "market visits", "art galleries", "wildlife watching"],
      localLandmarks: ["MONA", "kunanyi/Mt Wellington", "Salamanca Market", "Battery Point"],
      culturalNotes: ["Arts scene", "Foodie destination", "Heritage architecture"]
    },
    suggestions: {
      hotDay: ["Rare Hobart scorcher", "River swim time", "Cool mountain air above"],
      coldDay: ["Classic Hobart", "MONA day", "Whisky bar weather"],
      rainyDay: ["Perfect MONA weather", "Gallery and cafe time", "Cosy pub day"],
      mildDay: ["Kunanyi hike conditions", "Salamanca stroll", "Waterfront walk"]
    },
    avoid: ["beach swimming in winter"]
  },
  darwin: {
    id: "darwin",
    name: "Darwin",
    geography: {
      terrain: ["coastal", "tropical"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot, humid, monsoon season (Wet)",
      typicalWinter: "Warm and dry (Dry season)",
      rainyPeriod: "November to April (The Wet)"
    },
    context: {
      knownFor: ["The Wet and The Dry", "crocodiles", "sunsets", "proximity to Asia"],
      commonActivities: ["sunset markets", "croc spotting", "fishing", "swimming (in pools/safe areas)"],
      localLandmarks: ["Mindil Beach Markets", "Crocosaurus Cove", "Kakadu nearby"],
      culturalNotes: ["Relaxed Top End lifestyle", "Multicultural", "Close to SE Asia"]
    },
    suggestions: {
      hotDay: ["Standard Darwin! Find some aircon", "Pool time", "Wait for the sunset breeze"],
      coldDay: ["Darwin doesn't really do cold", "What Darwinites call 'jacket weather'"],
      rainyDay: ["The Wet doing its thing", "Storm watching time", "Indoor activities"],
      mildDay: ["Dry season perfection", "Mindil Markets weather", "Sunset viewing time"]
    },
    avoid: ["swimming in the ocean (crocs!)", "being outdoors midday in The Wet"]
  },
  canberra: {
    id: "canberra",
    name: "Canberra",
    geography: {
      terrain: ["inland", "plains", "mountainous"],
      nearOcean: false,
      nearMountains: true,
      // Snowy Mountains 2hrs away
      elevation: "moderate"
    },
    climate: {
      type: "continental",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Cold with frost"
    },
    context: {
      knownFor: ["Parliament", "galleries", "autumn colours", "cold winters"],
      commonActivities: ["gallery visits", "cycling", "bushwalking", "autumn leaf viewing"],
      localLandmarks: ["Parliament House", "War Memorial", "National Gallery", "Lake Burley Griffin"],
      culturalNotes: ["Political hub", "Well-planned city", "Strong cycling culture"]
    },
    suggestions: {
      hotDay: ["Lake swim time", "Gallery aircon", "Escape to the mountains"],
      coldDay: ["Classic Canberra winter", "Gallery day", "Cosy pub time", "Frost on the lawns"],
      rainyDay: ["Perfect museum weather", "Gallery hopping", "War Memorial visit"],
      mildDay: ["Perfect for the lake loop", "Arboretum walk", "Outdoor cafe weather"]
    },
    avoid: ["beach activities"]
  },
  gold_coast: {
    id: "gold_coast",
    name: "Gold Coast",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: true,
      // Hinterland
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Mild and pleasant"
    },
    context: {
      knownFor: ["Beaches", "theme parks", "surfing", "high-rises"],
      commonActivities: ["surfing", "beach going", "theme parks", "hinterland trips"],
      localLandmarks: ["Surfers Paradise", "Burleigh Heads", "Lamington National Park"],
      culturalNotes: ["Tourism hub", "Surf culture", "Retiree haven"]
    },
    suggestions: {
      hotDay: ["Beach day!", "Hit the waves", "Theme park pool", "Hinterland for cooler temps"],
      coldDay: ["Gold Coast cold? Still beach weather for most", "Cafe time"],
      rainyDay: ["Shopping arcade time", "Indoor theme park attractions"],
      mildDay: ["Perfect beach conditions", "Hinterland walk weather", "Outdoor dining"]
    },
    avoid: []
  },
  newcastle: {
    id: "newcastle",
    name: "Newcastle",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Warm to hot",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Beaches", "post-industrial renewal", "surf", "wine regions nearby"],
      commonActivities: ["beach going", "surfing", "cafe culture", "Hunter Valley trips"],
      localLandmarks: ["Nobbys Beach", "Newcastle Beach", "Fort Scratchley"],
      culturalNotes: ["Former steel city", "Renewal success story", "Growing food scene"]
    },
    suggestions: {
      hotDay: ["Beach time", "Ocean bath dip", "Hunter Valley escape"],
      coldDay: ["Cosy cafe time", "Wine country day trip"],
      rainyDay: ["Gallery day", "Pub lunch", "Shopping arcades"],
      mildDay: ["Perfect beach walk weather", "Coastal walk conditions", "Outdoor brunch"]
    },
    avoid: []
  },
  // === REGIONAL CITIES ===
  cairns: {
    id: "cairns",
    name: "Cairns",
    geography: {
      terrain: ["coastal", "tropical"],
      nearOcean: true,
      nearMountains: true,
      // Atherton Tablelands
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot, humid, wet season",
      typicalWinter: "Warm and dry",
      rainyPeriod: "December to April"
    },
    context: {
      knownFor: ["Great Barrier Reef", "Daintree", "tropical gateway"],
      commonActivities: ["reef trips", "rainforest visits", "swimming (stinger season aware)"],
      localLandmarks: ["Great Barrier Reef", "Daintree Rainforest", "Esplanade Lagoon"],
      culturalNotes: ["Tourism-driven", "International gateway", "Backpacker hub"]
    },
    suggestions: {
      hotDay: ["Lagoon time", "Reef trip (water is cooler)", "Tablelands escape"],
      coldDay: ["Cairns 'cold' - what southerners call warm", "Still great for the reef"],
      rainyDay: ["Wet season doing its thing", "Rainforest is meant to be wet!"],
      mildDay: ["Perfect reef conditions", "Rainforest walk weather", "Esplanade stroll"]
    },
    avoid: ["ocean swimming during stinger season without suit"]
  },
  townsville: {
    id: "townsville",
    name: "Townsville",
    geography: {
      terrain: ["coastal", "tropical"],
      nearOcean: true,
      nearMountains: true,
      // Castle Hill
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Warm and dry",
      rainyPeriod: "January to March"
    },
    context: {
      knownFor: ["Castle Hill", "Magnetic Island", "sunny days"],
      commonActivities: ["beach going", "Magnetic Island trips", "reef access"],
      localLandmarks: ["Castle Hill", "The Strand", "Magnetic Island"]
    },
    suggestions: {
      hotDay: ["Strand lagoon time", "Maggie Island trip", "Find the aircon"],
      coldDay: ["Townsville winter is lovely", "Perfect outdoor weather"],
      rainyDay: ["Wet season storm", "Indoor activities"],
      mildDay: ["Castle Hill walk", "Strand stroll", "Perfect North Queensland weather"]
    },
    avoid: ["ocean swimming during stinger season"]
  },
  sunshine_coast: {
    id: "sunshine_coast",
    name: "Sunshine Coast",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: true,
      // Hinterland
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "wet_dry",
      typicalSummer: "Warm and humid",
      typicalWinter: "Mild and pleasant"
    },
    context: {
      knownFor: ["Beaches", "hinterland", "relaxed lifestyle", "Noosa"],
      commonActivities: ["beach going", "surfing", "hinterland markets"],
      localLandmarks: ["Noosa", "Glass House Mountains", "Eumundi Markets"]
    },
    suggestions: {
      hotDay: ["Beach time", "Hinterland for cooler temps", "Noosa swim"],
      coldDay: ["Still beautiful on the Sunshine Coast", "Market browsing"],
      rainyDay: ["Hinterland cafe time", "Gallery visits"],
      mildDay: ["Perfect conditions", "Beach walk weather", "Markets day"]
    },
    avoid: []
  },
  wollongong: {
    id: "wollongong",
    name: "Wollongong",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      // Escarpment
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Escarpment", "beaches", "university town", "steel history"],
      commonActivities: ["beach going", "coastal walks", "escarpment hikes"],
      localLandmarks: ["Sea Cliff Bridge", "Nan Tien Temple", "Wollongong Lighthouse"]
    },
    suggestions: {
      hotDay: ["Beach day", "Rock pool dip", "Escarpment breezes"],
      coldDay: ["Cosy cafe time", "Temple visit"],
      rainyDay: ["Gallery time", "University area cafes"],
      mildDay: ["Coastal walk weather", "Sea Cliff Bridge drive", "Perfect Gong conditions"]
    },
    avoid: []
  },
  geelong: {
    id: "geelong",
    name: "Geelong",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      // Corio Bay
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm",
      typicalWinter: "Cool"
    },
    context: {
      knownFor: ["Waterfront", "Cats", "gateway to Great Ocean Road"],
      commonActivities: ["waterfront walks", "Cats games", "Great Ocean Road trips"],
      localLandmarks: ["Waterfront", "Bollards", "Eastern Beach"]
    },
    suggestions: {
      hotDay: ["Eastern Beach time", "Waterfront gelato", "Great Ocean Road drive"],
      coldDay: ["Footy season vibes", "Cosy pub lunch", "Waterfront rug up"],
      rainyDay: ["Gallery time", "Pub day", "Indoor markets"],
      mildDay: ["Waterfront walk", "Perfect for the bollards selfie", "Great Ocean Road weather"]
    },
    avoid: []
  },
  ballarat: {
    id: "ballarat",
    name: "Ballarat",
    geography: {
      terrain: ["inland", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm to hot",
      typicalWinter: "Cold with frost"
    },
    context: {
      knownFor: ["Gold rush history", "Sovereign Hill", "heritage", "cold winters"],
      commonActivities: ["Sovereign Hill", "heritage tours", "winter escapes"],
      localLandmarks: ["Sovereign Hill", "Lake Wendouree", "Eureka Centre"]
    },
    suggestions: {
      hotDay: ["Lake Wendouree shade", "Sovereign Hill mines are cool", "Find some aircon"],
      coldDay: ["Classic Ballarat", "Perfect for Sovereign Hill", "Cosy pub by the fire"],
      rainyDay: ["Museum weather", "Heritage building exploring"],
      mildDay: ["Lake walk conditions", "Garden exploring", "Perfect for the goldfields"]
    },
    avoid: ["beach activities"]
  },
  bendigo: {
    id: "bendigo",
    name: "Bendigo",
    geography: {
      terrain: ["inland", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Cold"
    },
    context: {
      knownFor: ["Gold history", "art gallery", "tram", "Chinese heritage"],
      commonActivities: ["gallery visits", "tram rides", "heritage walks", "wine regions"],
      localLandmarks: ["Central Deborah Gold Mine", "Bendigo Art Gallery", "Sacred Heart Cathedral"],
      culturalNotes: ["Strong arts scene", "Gold rush heritage", "Chinese Australian history"]
    },
    suggestions: {
      hotDay: ["Underground mine tour (naturally cool)", "Gallery aircon", "Find shade in the gardens"],
      coldDay: ["Classic regional Victoria", "Gallery day", "Cosy pub lunch"],
      rainyDay: ["Perfect gallery weather", "Heritage building exploring"],
      mildDay: ["Tram ride weather", "Heritage walk conditions", "Garden stroll"]
    },
    avoid: ["beach activities", "swimming unless at a pool"]
  },
  launceston: {
    id: "launceston",
    name: "Launceston",
    geography: {
      terrain: ["river_valley", "inland"],
      nearOcean: false,
      nearMountains: true,
      elevation: "low"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild",
      typicalWinter: "Cold"
    },
    context: {
      knownFor: ["Cataract Gorge", "wine regions", "heritage"],
      commonActivities: ["gorge walks", "wine tasting", "heritage exploring"],
      localLandmarks: ["Cataract Gorge", "Tamar Valley", "City Park"]
    },
    suggestions: {
      hotDay: ["Cataract Gorge pool", "Tamar Valley wine tour", "Riverside shade"],
      coldDay: ["Classic Tassie winter", "Cosy cellar door", "Pub by the fire"],
      rainyDay: ["Gallery time", "Wine tasting indoors"],
      mildDay: ["Gorge walk weather", "Perfect Tamar conditions", "Garden stroll"]
    },
    avoid: ["beach activities"]
  },
  albury: {
    id: "albury",
    name: "Albury",
    geography: {
      terrain: ["river_valley", "inland"],
      nearOcean: false,
      nearMountains: true,
      // Alpine region nearby
      elevation: "low"
    },
    climate: {
      type: "continental",
      seasons: "four_seasons",
      typicalSummer: "Hot",
      typicalWinter: "Cold with frost"
    },
    context: {
      knownFor: ["Murray River", "border town", "gateway to mountains"],
      commonActivities: ["river activities", "mountain trips", "cycling"],
      localLandmarks: ["Murray River", "Dean Street", "Noreuil Park"]
    },
    suggestions: {
      hotDay: ["River swim time", "Find the shade", "Head to the mountains for cooler temps"],
      coldDay: ["Classic inland cold", "Cosy cafe time", "Snow trip day?"],
      rainyDay: ["Gallery exploring", "Cafe hopping"],
      mildDay: ["River walk conditions", "Cycling weather", "Parklands picnic"]
    },
    avoid: ["beach activities"]
  },
  toowoomba: {
    id: "toowoomba",
    name: "Toowoomba",
    geography: {
      terrain: ["inland", "mountainous"],
      nearOcean: false,
      nearMountains: true,
      // Great Dividing Range
      elevation: "high"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Warm (cooler than coast)",
      typicalWinter: "Cool to cold"
    },
    context: {
      knownFor: ["Gardens", "flowers", "Carnival of Flowers", "elevated position"],
      commonActivities: ["garden visits", "cafe culture", "scenic drives"],
      localLandmarks: ["Queens Park", "Japanese Gardens", "Picnic Point"]
    },
    suggestions: {
      hotDay: ["Cooler than the coast at least!", "Garden shade", "Cafe aircon"],
      coldDay: ["Brisk Toowoomba morning", "Cosy cafe time"],
      rainyDay: ["Garden of Flowers needs rain!", "Gallery time"],
      mildDay: ["Perfect garden weather", "Picnic Point views", "Cafe hopping"]
    },
    avoid: ["beach activities"]
  },
  alice_springs: {
    id: "alice_springs",
    name: "Alice Springs",
    geography: {
      terrain: ["desert", "inland"],
      nearOcean: false,
      nearMountains: true,
      // MacDonnell Ranges
      elevation: "moderate"
    },
    climate: {
      type: "arid",
      seasons: "four_seasons",
      typicalSummer: "Extremely hot and dry",
      typicalWinter: "Cold nights, warm days"
    },
    context: {
      knownFor: ["Outback", "Uluru nearby", "MacDonnell Ranges", "Aboriginal culture"],
      commonActivities: ["gorge swimming", "outback tours", "stargazing", "cultural experiences"],
      localLandmarks: ["Uluru (4.5hrs)", "MacDonnell Ranges", "Alice Springs Desert Park"],
      culturalNotes: ["Heart of Australia", "Strong Aboriginal heritage", "Remote community"]
    },
    suggestions: {
      hotDay: ["Stay in the aircon until evening", "Dawn or dusk activities only", "Gorge swimming if water is in"],
      coldDay: ["Desert cold - rug up at night", "Perfect daytime touring weather"],
      rainyDay: ["Rare desert rain! Watch for flooding", "Check gorge water levels"],
      mildDay: ["Perfect outback conditions", "Ranges walk weather", "Stargazing tonight"]
    },
    avoid: ["beach activities", "midday activities in summer", "ocean references"]
  },
  broome: {
    id: "broome",
    name: "Broome",
    geography: {
      terrain: ["coastal", "tropical", "desert"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "arid",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid (The Wet)",
      typicalWinter: "Warm and dry (The Dry)",
      rainyPeriod: "November to March"
    },
    context: {
      knownFor: ["Cable Beach", "pearling", "camels", "Staircase to the Moon"],
      commonActivities: ["beach sunset", "camel rides", "pearling tours"],
      localLandmarks: ["Cable Beach", "Chinatown", "Gantheaume Point"],
      culturalNotes: ["Pearling heritage", "Multicultural history", "Remote tropical paradise"]
    },
    suggestions: {
      hotDay: ["Beach time (croc aware zones)", "Pool time", "Wait for sunset"],
      coldDay: ["Broome doesn't really do cold!", "Perfect touring weather"],
      rainyDay: ["The Wet season", "Pearl gallery time"],
      mildDay: ["Cable Beach perfection", "Camel ride weather", "Staircase to the Moon?"]
    },
    avoid: ["swimming outside designated safe areas (crocs)"]
  },
  mackay: {
    id: "mackay",
    name: "Mackay",
    geography: {
      terrain: ["coastal", "tropical"],
      nearOcean: true,
      nearMountains: true,
      // Eungella nearby
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Mild",
      rainyPeriod: "January to March"
    },
    context: {
      knownFor: ["Sugar cane", "platypus", "reef access", "Eungella"],
      commonActivities: ["beach going", "Eungella platypus spotting", "reef trips"],
      localLandmarks: ["Eungella National Park", "Cape Hillsborough", "Harbour"]
    },
    suggestions: {
      hotDay: ["Beach time", "Eungella for cooler temps", "Pool day"],
      coldDay: ["Mackay winter is still pleasant", "Perfect touring weather"],
      rainyDay: ["Wet season storms", "Indoor activities"],
      mildDay: ["Perfect conditions", "Platypus spotting weather", "Beach walk"]
    },
    avoid: ["ocean swimming during stinger season"]
  },
  rockhampton: {
    id: "rockhampton",
    name: "Rockhampton",
    geography: {
      terrain: ["inland", "tropical", "river_valley"],
      nearOcean: false,
      // 40km from coast
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Mild and dry"
    },
    context: {
      knownFor: ["Beef capital", "Capricorn Caves", "Tropic of Capricorn"],
      commonActivities: ["caves tours", "steakhouse dining", "river activities"],
      localLandmarks: ["Capricorn Caves", "Botanic Gardens", "Dreamtime Centre"]
    },
    suggestions: {
      hotDay: ["Caves are naturally cool!", "Find the aircon", "River shade"],
      coldDay: ["Rocky winter - still warm", "Perfect touring weather"],
      rainyDay: ["Cave tours still on", "Museum time"],
      mildDay: ["Gardens weather", "River walk", "Perfect for the caves"]
    },
    avoid: ["beach activities (40km from coast)"]
  },
  bunbury: {
    id: "bunbury",
    name: "Bunbury",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "four_seasons",
      typicalSummer: "Warm and dry",
      typicalWinter: "Mild and wet"
    },
    context: {
      knownFor: ["Dolphins", "beaches", "Ferguson Valley wine"],
      commonActivities: ["dolphin watching", "beach going", "wine tasting"],
      localLandmarks: ["Dolphin Discovery Centre", "Koombana Bay", "Ferguson Valley"]
    },
    suggestions: {
      hotDay: ["Beach day", "Dolphin watching", "Ferguson Valley escape"],
      coldDay: ["Cosy winery lunch", "Cafe time"],
      rainyDay: ["Dolphin centre", "Wine tasting weather"],
      mildDay: ["Perfect dolphin conditions", "Beach walk", "Valley drive"]
    },
    avoid: []
  },
  // === MORE AUSTRALIAN TOWNS ===
  // New South Wales
  dubbo: {
    id: "dubbo",
    name: "Dubbo",
    geography: {
      terrain: ["inland", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Cool with frost"
    },
    context: {
      knownFor: ["Taronga Western Plains Zoo", "Old Dubbo Gaol", "regional hub"],
      commonActivities: ["zoo visits", "heritage walks", "stargazing"],
      localLandmarks: ["Western Plains Zoo", "Old Dubbo Gaol", "Macquarie River"],
      culturalNotes: ["Major western NSW hub", "Agricultural centre"]
    },
    suggestions: {
      hotDay: ["Zoo early morning", "Find the aircon", "River shade"],
      coldDay: ["Frosty Dubbo morning", "Heritage walk", "Cosy pub lunch"],
      rainyDay: ["Museum time", "Gallery visits", "Shopping day"],
      mildDay: ["Perfect zoo weather", "River walk", "Heritage trail"]
    },
    avoid: ["beach activities", "ocean references"]
  },
  tamworth: {
    id: "tamworth",
    name: "Tamworth",
    geography: {
      terrain: ["inland", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot",
      typicalWinter: "Cool to cold"
    },
    context: {
      knownFor: ["Country music", "Golden Guitar", "Big Golden Guitar"],
      commonActivities: ["country music events", "heritage walks", "equestrian"],
      localLandmarks: ["Big Golden Guitar", "Walk a Country Mile", "Tamworth Regional Gallery"],
      culturalNotes: ["Country music capital of Australia", "Annual Country Music Festival"]
    },
    suggestions: {
      hotDay: ["Air-conditioned venues", "Evening country music", "Pool time"],
      coldDay: ["Live music venues", "Gallery time", "Cosy pub"],
      rainyDay: ["Country music museum", "Gallery day", "Live music"],
      mildDay: ["Walk a Country Mile", "Outdoor concerts", "Explore the town"]
    },
    avoid: ["beach activities", "ocean references"]
  },
  wagga_wagga: {
    id: "wagga_wagga",
    name: "Wagga Wagga",
    geography: {
      terrain: ["inland", "river_valley"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot",
      typicalWinter: "Cold with frost"
    },
    context: {
      knownFor: ["Murrumbidgee River", "wine region", "RAAF base"],
      commonActivities: ["river activities", "wine tasting", "cycling"],
      localLandmarks: ["Wagga Beach", "Botanic Gardens", "National Art Glass Gallery"],
      culturalNotes: ["Largest inland city in NSW", "University town"]
    },
    suggestions: {
      hotDay: ["Wagga Beach (river)", "Find the aircon", "Evening river walk"],
      coldDay: ["Frosty Wagga morning", "Gallery visit", "Wine tasting"],
      rainyDay: ["Art Glass Gallery", "Museum time", "Cafe hopping"],
      mildDay: ["River walk", "Botanic Gardens", "Cycling weather"]
    },
    avoid: ["ocean beach references"]
  },
  orange: {
    id: "orange",
    name: "Orange",
    geography: {
      terrain: ["inland", "mountainous"],
      nearOcean: false,
      nearMountains: true,
      elevation: "high"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm (cooler than lowlands)",
      typicalWinter: "Cold with occasional snow"
    },
    context: {
      knownFor: ["Food and wine", "cold climate wines", "cherries", "apples"],
      commonActivities: ["wine tasting", "food festivals", "orchard visits"],
      localLandmarks: ["Mount Canobolas", "Cook Park", "Orange Botanic Gardens"],
      culturalNotes: ["Food and wine destination", "Four distinct seasons"]
    },
    suggestions: {
      hotDay: ["Cooler than Sydney up here!", "Wine cellar temperatures", "Mountain breezes"],
      coldDay: ["Classic Orange winter", "Cellar door fires", "Hearty food weather"],
      rainyDay: ["Perfect wine tasting weather", "Cosy restaurant day"],
      mildDay: ["Orchard weather", "Gardens walk", "Perfect wine touring"]
    },
    avoid: ["beach activities"]
  },
  port_macquarie: {
    id: "port_macquarie",
    name: "Port Macquarie",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Warm and humid",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Koalas", "beaches", "coastal lifestyle"],
      commonActivities: ["beach going", "koala hospital visits", "coastal walks"],
      localLandmarks: ["Koala Hospital", "Sea Acres Rainforest Centre", "Town Beach"],
      culturalNotes: ["Retirement and holiday destination", "Family-friendly"]
    },
    suggestions: {
      hotDay: ["Beach time", "Koala Hospital visit", "Rock pool exploring"],
      coldDay: ["Rainforest walk", "Koala visit", "Cosy cafe"],
      rainyDay: ["Koala Hospital", "Rainforest Centre", "Indoor activities"],
      mildDay: ["Coastal walk", "Beach stroll", "Kayaking weather"]
    },
    avoid: []
  },
  coffs_harbour: {
    id: "coffs_harbour",
    name: "Coffs Harbour",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Warm and humid",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Big Banana", "whale watching", "beaches"],
      commonActivities: ["beach going", "whale watching", "rainforest walks"],
      localLandmarks: ["Big Banana", "Muttonbird Island", "Solitary Islands Marine Park"],
      culturalNotes: ["Family holiday destination", "Banana growing region"]
    },
    suggestions: {
      hotDay: ["Beach day", "Big Banana ice cream", "Marine park snorkeling"],
      coldDay: ["Rainforest walk", "Big Banana visit", "Whale watching (winter)"],
      rainyDay: ["Big Banana complex", "Aquarium", "Indoor activities"],
      mildDay: ["Muttonbird Island walk", "Beach stroll", "Whale watching"]
    },
    avoid: []
  },
  broken_hill: {
    id: "broken_hill",
    name: "Broken Hill",
    geography: {
      terrain: ["desert", "inland"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "arid",
      seasons: "four_seasons",
      typicalSummer: "Extremely hot",
      typicalWinter: "Cool days, cold nights"
    },
    context: {
      knownFor: ["Mining heritage", "outback arts", "Silverton", "Mad Max"],
      commonActivities: ["art galleries", "mining heritage", "outback tours"],
      localLandmarks: ["Line of Lode", "Pro Hart Gallery", "Silverton"],
      culturalNotes: ["NSW but on SA time", "Strong arts community", "Mining town heritage"]
    },
    suggestions: {
      hotDay: ["Gallery aircon", "Stay indoors until evening", "Underground mine tour"],
      coldDay: ["Desert cold at night", "Gallery day", "Heritage walk"],
      rainyDay: ["Rare outback rain!", "Gallery hopping"],
      mildDay: ["Silverton trip", "Line of Lode walk", "Perfect outback weather"]
    },
    avoid: ["beach activities", "ocean references", "swimming suggestions"]
  },
  // Victoria
  shepparton: {
    id: "shepparton",
    name: "Shepparton",
    geography: {
      terrain: ["inland", "plains", "river_valley"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot",
      typicalWinter: "Cool with fog"
    },
    context: {
      knownFor: ["Fruit bowl of Australia", "SPC", "multicultural community"],
      commonActivities: ["fruit picking", "river activities", "markets"],
      localLandmarks: ["SAM (Shepparton Art Museum)", "Victoria Park Lake", "SPC Ardmona"],
      culturalNotes: ["Strong multicultural community", "Agricultural hub"]
    },
    suggestions: {
      hotDay: ["Lake swimming", "Find the aircon", "Fruit picking early morning"],
      coldDay: ["Foggy Shepp morning", "Gallery visit", "Cosy cafe"],
      rainyDay: ["SAM gallery", "Shopping day", "Cafe hopping"],
      mildDay: ["Lake walk", "Markets", "Orchard visiting"]
    },
    avoid: ["beach activities", "ocean references"]
  },
  warrnambool: {
    id: "warrnambool",
    name: "Warrnambool",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild to warm",
      typicalWinter: "Cool and wet"
    },
    context: {
      knownFor: ["Whales", "Great Ocean Road", "historic shipwrecks"],
      commonActivities: ["whale watching", "beach going", "historic tours"],
      localLandmarks: ["Flagstaff Hill", "Logan's Beach", "Tower Hill"],
      culturalNotes: ["Southern right whale nursery", "Great Ocean Road endpoint"]
    },
    suggestions: {
      hotDay: ["Beach day", "Logan's Beach whale watching", "Cool ocean breezes"],
      coldDay: ["Whale watching season!", "Flagstaff Hill", "Cosy pub"],
      rainyDay: ["Flagstaff Hill museum", "Gallery time", "Indoor heritage"],
      mildDay: ["Tower Hill walk", "Beach stroll", "Whale watching"]
    },
    avoid: []
  },
  mildura: {
    id: "mildura",
    name: "Mildura",
    geography: {
      terrain: ["inland", "desert", "river_valley"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Very hot",
      typicalWinter: "Mild days, cool nights"
    },
    context: {
      knownFor: ["Murray River", "dried fruits", "wine", "paddle steamers"],
      commonActivities: ["river cruises", "wine tasting", "houseboats"],
      localLandmarks: ["Murray River", "Mungo National Park nearby", "Rio Vista"],
      culturalNotes: ["Food bowl region", "Irrigation pioneer town"]
    },
    suggestions: {
      hotDay: ["Murray River swim", "Houseboat life", "Find the aircon"],
      coldDay: ["Wine tasting", "Murray River cruise", "Heritage walk"],
      rainyDay: ["Gallery time", "Wine tasting", "Rio Vista tour"],
      mildDay: ["River cruise", "Cycling", "Wine touring"]
    },
    avoid: ["ocean beaches", "coastal references"]
  },
  echuca: {
    id: "echuca",
    name: "Echuca",
    geography: {
      terrain: ["inland", "river_valley"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot",
      typicalWinter: "Cool with fog"
    },
    context: {
      knownFor: ["Paddle steamers", "Port of Echuca", "Murray River"],
      commonActivities: ["paddle steamer cruises", "port heritage", "river activities"],
      localLandmarks: ["Port of Echuca", "Historic Wharf", "PS Emmylou"],
      culturalNotes: ["Historic river port", "Paddle steamer heritage"]
    },
    suggestions: {
      hotDay: ["River swimming", "Paddle steamer with a breeze", "Find shade at the port"],
      coldDay: ["Foggy river morning", "Port heritage tour", "Pub by the fire"],
      rainyDay: ["Port museum", "Heritage displays", "Cosy pub"],
      mildDay: ["Paddle steamer cruise", "Port walk", "River cycling"]
    },
    avoid: ["ocean beaches", "coastal references"]
  },
  // Queensland
  bundaberg: {
    id: "bundaberg",
    name: "Bundaberg",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Mild and dry"
    },
    context: {
      knownFor: ["Bundaberg Rum", "turtles", "sugar cane"],
      commonActivities: ["turtle watching", "rum distillery", "beach trips"],
      localLandmarks: ["Bundaberg Rum Distillery", "Mon Repos turtle centre", "Bargara Beach"],
      culturalNotes: ["Rum capital", "Turtle nesting season Nov-Mar"]
    },
    suggestions: {
      hotDay: ["Bargara Beach", "Rum distillery tour (airconditioned!)", "Evening turtle watching"],
      coldDay: ["Bundy winter is mild", "Distillery tour", "Beach walk"],
      rainyDay: ["Rum distillery", "Museum time", "Indoor activities"],
      mildDay: ["Turtle watching", "Beach day", "Cane farm touring"]
    },
    avoid: []
  },
  gladstone: {
    id: "gladstone",
    name: "Gladstone",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Mild and dry"
    },
    context: {
      knownFor: ["Gateway to Southern Great Barrier Reef", "port city", "Heron Island"],
      commonActivities: ["reef trips", "island visits", "fishing"],
      localLandmarks: ["Auckland Point", "Tondoon Botanic Gardens", "Heron Island nearby"],
      culturalNotes: ["Industrial port city", "Reef access point"]
    },
    suggestions: {
      hotDay: ["Island escape", "Pool time", "Air-conditioned activities"],
      coldDay: ["Gladstone doesn't really do cold", "Perfect reef weather"],
      rainyDay: ["Museum time", "Indoor activities"],
      mildDay: ["Reef day trip", "Botanic gardens", "Waterfront walk"]
    },
    avoid: []
  },
  hervey_bay: {
    id: "hervey_bay",
    name: "Hervey Bay",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "wet_dry",
      typicalSummer: "Warm and humid",
      typicalWinter: "Mild and pleasant"
    },
    context: {
      knownFor: ["Whale watching capital", "Fraser Island gateway", "calm beaches"],
      commonActivities: ["whale watching", "Fraser Island trips", "fishing"],
      localLandmarks: ["Fraser Island", "Urangan Pier", "Whale watching boats"],
      culturalNotes: ["Whale watching capital of Australia", "Retirement destination"]
    },
    suggestions: {
      hotDay: ["Calm beach swim", "Fraser Island adventure", "Sea breeze"],
      coldDay: ["Whale watching season!", "Fraser Island trip", "Pier walk"],
      rainyDay: ["Indoor activities", "Aquarium", "Cafe time"],
      mildDay: ["Whale watching (Jul-Nov)", "Beach walk", "Pier fishing"]
    },
    avoid: []
  },
  mount_isa: {
    id: "mount_isa",
    name: "Mount Isa",
    geography: {
      terrain: ["desert", "inland"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "arid",
      seasons: "wet_dry",
      typicalSummer: "Extremely hot",
      typicalWinter: "Warm days, cool nights"
    },
    context: {
      knownFor: ["Mining", "outback", "rodeo"],
      commonActivities: ["mine tours", "outback experiences", "rodeo"],
      localLandmarks: ["Hard Times Mine", "Lake Moondarra", "Outback at Isa"],
      culturalNotes: ["Mining town", "Outback Queensland hub", "Famous rodeo"]
    },
    suggestions: {
      hotDay: ["Underground mine tour", "Lake Moondarra early", "Air-con essential"],
      coldDay: ["Mount Isa cold is still warm", "Mine tour", "Outback at Isa"],
      rainyDay: ["Rare outback rain!", "Underground tours", "Museum"],
      mildDay: ["Lake Moondarra", "Outback touring", "Mine tour"]
    },
    avoid: ["beach activities", "ocean references", "coastal suggestions"]
  },
  // South Australia
  port_lincoln: {
    id: "port_lincoln",
    name: "Port Lincoln",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "four_seasons",
      typicalSummer: "Warm and dry",
      typicalWinter: "Cool and wet"
    },
    context: {
      knownFor: ["Seafood", "tuna capital", "shark cage diving"],
      commonActivities: ["shark cage diving", "seafood dining", "fishing"],
      localLandmarks: ["Boston Bay", "Coffin Bay nearby", "Marina"],
      culturalNotes: ["Seafood capital of Australia", "Tuna industry"]
    },
    suggestions: {
      hotDay: ["Beach day", "Seafood on the marina", "Water activities"],
      coldDay: ["Cosy seafood restaurant", "Marina walk", "Whale watching (winter)"],
      rainyDay: ["Seafood lunch", "Indoor activities", "Fresh oysters"],
      mildDay: ["Shark cage diving", "Coffin Bay trip", "Marina stroll"]
    },
    avoid: []
  },
  mount_gambier: {
    id: "mount_gambier",
    name: "Mount Gambier",
    geography: {
      terrain: ["inland", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm",
      typicalWinter: "Cool and wet"
    },
    context: {
      knownFor: ["Blue Lake", "sinkholes", "caves", "limestone coast"],
      commonActivities: ["Blue Lake viewing", "cave diving", "sinkhole gardens"],
      localLandmarks: ["Blue Lake", "Umpherston Sinkhole", "Cave Gardens"],
      culturalNotes: ["Blue Lake changes colour seasonally", "Volcanic region"]
    },
    suggestions: {
      hotDay: ["Cave coolness", "Sinkhole gardens shade", "Blue Lake viewpoint"],
      coldDay: ["Classic Mount Gambier", "Cave exploring", "Cosy cafe"],
      rainyDay: ["Cave exploring", "Museum time", "Indoor activities"],
      mildDay: ["Blue Lake walk", "Sinkhole gardens", "Cave touring"]
    },
    avoid: ["beach activities"]
  },
  whyalla: {
    id: "whyalla",
    name: "Whyalla",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Giant cuttlefish", "steelworks", "Eyre Peninsula"],
      commonActivities: ["cuttlefish diving", "beach going", "fishing"],
      localLandmarks: ["HMAS Whyalla", "Cuttlefish diving site", "Foreshore"],
      culturalNotes: ["Steel city", "Unique cuttlefish aggregation May-Aug"]
    },
    suggestions: {
      hotDay: ["Beach day", "Foreshore walk evening", "Find the aircon"],
      coldDay: ["Cuttlefish season!", "HMAS Whyalla tour", "Cosy pub"],
      rainyDay: ["Museum visit", "Indoor activities"],
      mildDay: ["Cuttlefish diving (winter)", "Foreshore walk", "Beach time"]
    },
    avoid: []
  },
  // Western Australia
  geraldton: {
    id: "geraldton",
    name: "Geraldton",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "semi_arid",
      seasons: "four_seasons",
      typicalSummer: "Hot and windy",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Wind", "HMAS Sydney memorial", "wildflowers", "Abrolhos Islands"],
      commonActivities: ["kitesurfing", "windsurfing", "memorial visits"],
      localLandmarks: ["HMAS Sydney II Memorial", "Abrolhos Islands", "Point Moore Lighthouse"],
      culturalNotes: ["Wind sport capital", "Wildflower season gateway"]
    },
    suggestions: {
      hotDay: ["Beach day with sea breeze", "Windsurfing", "Cool ocean water"],
      coldDay: ["Geraldton cold is still nice", "Memorial visit", "Museum"],
      rainyDay: ["Museum of Geraldton", "Gallery time", "Indoor activities"],
      mildDay: ["Perfect wind sport conditions", "Memorial walk", "Abrolhos trip"]
    },
    avoid: []
  },
  kalgoorlie: {
    id: "kalgoorlie",
    name: "Kalgoorlie",
    geography: {
      terrain: ["desert", "inland"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "arid",
      seasons: "four_seasons",
      typicalSummer: "Extremely hot",
      typicalWinter: "Cool days, cold nights"
    },
    context: {
      knownFor: ["Gold mining", "Super Pit", "outback heritage"],
      commonActivities: ["mine tours", "heritage walks", "bush experiences"],
      localLandmarks: ["Super Pit", "Hannan Street", "Museum of the Goldfields"],
      culturalNotes: ["Gold rush heritage", "Historic pubs", "Mining town character"]
    },
    suggestions: {
      hotDay: ["Super Pit viewing early morning", "Museum aircon", "Evening pub"],
      coldDay: ["Desert cold! Rug up at night", "Mine tour", "Heritage walk"],
      rainyDay: ["Rare goldfields rain", "Museum", "Historic pub tour"],
      mildDay: ["Super Pit viewing", "Heritage walk", "Bush experience"]
    },
    avoid: ["beach activities", "ocean references", "coastal suggestions"]
  },
  albany: {
    id: "albany",
    name: "Albany",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild to warm",
      typicalWinter: "Cool and wet"
    },
    context: {
      knownFor: ["ANZAC history", "whales", "natural beauty", "oldest settlement in WA"],
      commonActivities: ["whale watching", "ANZAC tours", "coastal walks"],
      localLandmarks: ["National ANZAC Centre", "The Gap", "Natural Bridge", "Whale World"],
      culturalNotes: ["ANZAC departure point", "First WA settlement"]
    },
    suggestions: {
      hotDay: ["Beach day", "Coastal breezes", "Whale watching"],
      coldDay: ["Classic Albany winter", "ANZAC Centre", "Whale World"],
      rainyDay: ["ANZAC Centre", "Whale World museum", "Cosy pub"],
      mildDay: ["The Gap and Natural Bridge", "Coastal walk", "Whale watching"]
    },
    avoid: []
  },
  // Tasmania
  devonport: {
    id: "devonport",
    name: "Devonport",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild",
      typicalWinter: "Cool"
    },
    context: {
      knownFor: ["Spirit of Tasmania", "gateway to Tassie", "Cradle Mountain access"],
      commonActivities: ["ferry travel", "Cradle Mountain trips", "coastal walks"],
      localLandmarks: ["Spirit of Tasmania terminal", "Mersey Bluff", "Home Hill"],
      culturalNotes: ["Gateway to Tasmania", "Ferry terminal city"]
    },
    suggestions: {
      hotDay: ["Beach day", "Coastal breezes", "Mersey Bluff walk"],
      coldDay: ["Classic Tassie weather", "Home Hill museum", "Cosy cafe"],
      rainyDay: ["Museum time", "Gallery visits", "Indoor activities"],
      mildDay: ["Mersey Bluff walk", "Cradle Mountain day trip", "Coastal exploring"]
    },
    avoid: []
  },
  burnie: {
    id: "burnie",
    name: "Burnie",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild",
      typicalWinter: "Cool"
    },
    context: {
      knownFor: ["Penguins", "makers trail", "paper mill heritage"],
      commonActivities: ["penguin viewing", "makers trail", "coastal walks"],
      localLandmarks: ["Penguin Observation Centre", "Makers Workshop", "Emu Valley Rhododendron Garden"],
      culturalNotes: ["Penguin colony", "Creative makers hub"]
    },
    suggestions: {
      hotDay: ["Beach day", "Coastal walk", "Makers trail"],
      coldDay: ["Penguin viewing (dusk)", "Makers Workshop", "Cosy cafe"],
      rainyDay: ["Makers Workshop", "Gallery time", "Indoor crafts"],
      mildDay: ["Penguin viewing", "Rhododendron garden", "Makers trail"]
    },
    avoid: []
  },
  // Northern Territory
  katherine: {
    id: "katherine",
    name: "Katherine",
    geography: {
      terrain: ["tropical", "inland"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and very wet (The Wet)",
      typicalWinter: "Warm and dry (The Dry)",
      rainyPeriod: "November to April"
    },
    context: {
      knownFor: ["Nitmiluk (Katherine Gorge)", "hot springs", "outback gateway"],
      commonActivities: ["gorge cruises", "hot springs", "bushwalking"],
      localLandmarks: ["Nitmiluk National Park", "Katherine Hot Springs", "Cutta Cutta Caves"],
      culturalNotes: ["Gateway to the outback", "Strong Indigenous culture"]
    },
    suggestions: {
      hotDay: ["Hot springs (yes, really!)", "Gorge cruise for breezes", "Find aircon"],
      coldDay: ["Katherine cold? That's just warm", "Perfect touring weather"],
      rainyDay: ["The Wet doing its thing", "Caves tour", "Wait it out"],
      mildDay: ["Nitmiluk cruise", "Hot springs", "Bushwalking"]
    },
    avoid: ["beach activities", "ocean swimming", "swimming in gorge without checking for crocs"]
  },
  tennant_creek: {
    id: "tennant_creek",
    name: "Tennant Creek",
    geography: {
      terrain: ["desert", "inland"],
      nearOcean: false,
      nearMountains: false,
      elevation: "moderate"
    },
    climate: {
      type: "arid",
      seasons: "wet_dry",
      typicalSummer: "Extremely hot",
      typicalWinter: "Warm days, cool nights"
    },
    context: {
      knownFor: ["Gold mining", "Devils Marbles", "outback", "Indigenous culture"],
      commonActivities: ["Devils Marbles visit", "outback experiences", "stargazing"],
      localLandmarks: ["Devils Marbles (Karlu Karlu)", "Battery Hill Mining Centre", "Nyinkka Nyunyu"],
      culturalNotes: ["Outback crossroads", "Strong Indigenous heritage"]
    },
    suggestions: {
      hotDay: ["Dawn or dusk only", "Mining Centre aircon", "Don't go outside midday"],
      coldDay: ["Desert nights are cold!", "Perfect Devils Marbles weather", "Stargazing"],
      rainyDay: ["Rare desert rain", "Cultural centre", "Mining museum"],
      mildDay: ["Devils Marbles sunrise/sunset", "Outback touring", "Stargazing"]
    },
    avoid: ["beach activities", "ocean references", "midday outdoor activities in summer"]
  }
};
var INTERNATIONAL_PROFILES = {
  // === NEW ZEALAND ===
  auckland: {
    id: "auckland",
    name: "Auckland",
    geography: {
      terrain: ["coastal"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm and humid",
      typicalWinter: "Mild and wet"
    },
    context: {
      knownFor: ["City of Sails", "volcanoes", "harbours", "diverse culture"],
      commonActivities: ["sailing", "hiking volcanoes", "beach going", "wine regions"],
      localLandmarks: ["Sky Tower", "Rangitoto Island", "Waiheke Island"],
      culturalNotes: ["Largest Polynesian city in the world", "Rugby culture"]
    },
    suggestions: {
      hotDay: ["Beach day", "Waiheke Island trip", "Harbour sailing"],
      coldDay: ["Museum time", "Sky Tower visit", "Cosy cafe"],
      rainyDay: ["Classic Auckland", "Museum or gallery", "Shopping arcades"],
      mildDay: ["Rangitoto climb", "Harbour walk", "Wine tasting"]
    },
    avoid: []
  },
  wellington: {
    id: "wellington",
    name: "Wellington",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm but windy",
      typicalWinter: "Cool and very windy"
    },
    context: {
      knownFor: ["Wind", "coffee culture", "creative capital", "Te Papa"],
      commonActivities: ["cafe hopping", "museum visits", "harbour walks"],
      localLandmarks: ["Te Papa", "Cable Car", "Mount Victoria", "Beehive"],
      culturalNotes: ["Windiest city", "Coffee capital of NZ", "Film industry hub"]
    },
    suggestions: {
      hotDay: ["Beach at Oriental Bay", "Cable car for breezes", "Harbour walk"],
      coldDay: ["Te Papa day", "Cable car", "Cosy Cuba Street cafe"],
      rainyDay: ["Te Papa", "Gallery hopping", "Cuba Street cafes"],
      mildDay: ["Mount Vic walk", "Harbour stroll", "Cable car ride"]
    },
    avoid: []
  },
  christchurch: {
    id: "christchurch",
    name: "Christchurch",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm and dry",
      typicalWinter: "Cool with frosts"
    },
    context: {
      knownFor: ["Garden city", "rebuild", "gateway to South Island"],
      commonActivities: ["cycling", "punting", "gardens", "skiing nearby"],
      localLandmarks: ["Botanic Gardens", "Cardboard Cathedral", "Port Hills"],
      culturalNotes: ["Earthquake rebuild", "English heritage", "Cycling culture"]
    },
    suggestions: {
      hotDay: ["Sumner Beach", "Botanic Gardens shade", "Punting on the Avon"],
      coldDay: ["Frosty Christchurch", "Museum time", "Ski trip?"],
      rainyDay: ["Canterbury Museum", "Art Gallery", "Cafe hopping"],
      mildDay: ["Botanic Gardens", "Port Hills walk", "Punting"]
    },
    avoid: []
  },
  // === ASIA-PACIFIC ===
  singapore: {
    id: "singapore",
    name: "Singapore",
    geography: {
      terrain: ["coastal", "tropical"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "mild_year_round",
      typicalSummer: "Hot and humid year-round",
      typicalWinter: "Hot and humid year-round",
      rainyPeriod: "November to January (monsoon)"
    },
    context: {
      knownFor: ["Gardens by the Bay", "food culture", "cleanliness", "shopping"],
      commonActivities: ["hawker centres", "shopping", "gardens", "night safari"],
      localLandmarks: ["Marina Bay Sands", "Gardens by the Bay", "Sentosa"],
      culturalNotes: ["Food obsessed", "Multicultural", "Strict laws"]
    },
    suggestions: {
      hotDay: ["Standard Singapore!", "Mall aircon", "Pool time", "Evening hawker food"],
      coldDay: ["Singapore doesn't do cold", "Enjoy the aircon"],
      rainyDay: ["Monsoon season", "Mall hopping", "Hawker centre"],
      mildDay: ["Gardens by the Bay early", "Sentosa", "Botanic Gardens"]
    },
    avoid: ["cold weather references", "winter activities", "seasonal change references"]
  },
  tokyo: {
    id: "tokyo",
    name: "Tokyo",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Hot and very humid",
      typicalWinter: "Cool and dry",
      rainyPeriod: "June-July (tsuyu/rainy season)"
    },
    context: {
      knownFor: ["Technology", "food", "temples", "cherry blossoms"],
      commonActivities: ["temple visits", "food tours", "shopping", "cherry blossom viewing"],
      localLandmarks: ["Senso-ji", "Tokyo Tower", "Shibuya Crossing", "Mt Fuji views"],
      culturalNotes: ["Four distinct seasons celebrated", "Cherry blossom season is huge"]
    },
    suggestions: {
      hotDay: ["Mall aircon", "Evening activities", "Beer garden season"],
      coldDay: ["Ramen season", "Onsen trip", "Hot pot weather"],
      rainyDay: ["Tsuyu season", "Underground shopping", "Museum day"],
      mildDay: ["Cherry blossom viewing (spring)", "Temple walks", "Garden visits"]
    },
    avoid: []
  },
  hong_kong: {
    id: "hong_kong",
    name: "Hong Kong",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "subtropical",
      seasons: "four_seasons",
      typicalSummer: "Hot and very humid with typhoons",
      typicalWinter: "Cool and dry",
      rainyPeriod: "May to September"
    },
    context: {
      knownFor: ["Skyline", "dim sum", "shopping", "hiking"],
      commonActivities: ["dim sum", "hiking", "shopping", "harbour views"],
      localLandmarks: ["Victoria Peak", "Star Ferry", "Big Buddha"],
      culturalNotes: ["East meets West", "Food paradise", "Typhoon season"]
    },
    suggestions: {
      hotDay: ["Mall aircon", "Star Ferry breeze", "Pool time"],
      coldDay: ["Hong Kong cold - still mild!", "Hot pot season", "Peak views"],
      rainyDay: ["Typhoon watch?", "Mall hopping", "Dim sum"],
      mildDay: ["Peak hike", "Lantau Island", "Harbour walk"]
    },
    avoid: []
  },
  seoul: {
    id: "seoul",
    name: "Seoul",
    geography: {
      terrain: ["river_valley", "mountainous"],
      nearOcean: false,
      nearMountains: true,
      elevation: "low"
    },
    climate: {
      type: "continental",
      seasons: "four_seasons",
      typicalSummer: "Hot and humid",
      typicalWinter: "Very cold",
      rainyPeriod: "July-August (monsoon)"
    },
    context: {
      knownFor: ["K-pop", "palaces", "food", "technology"],
      commonActivities: ["palace visits", "K-beauty shopping", "street food", "hiking"],
      localLandmarks: ["Gyeongbokgung Palace", "N Seoul Tower", "Bukchon Hanok Village"],
      culturalNotes: ["Very cold winters", "Four distinct seasons", "Cafe culture"]
    },
    suggestions: {
      hotDay: ["Monsoon season?", "Mall aircon", "Bingsu (shaved ice)"],
      coldDay: ["Bundle up!", "Hot jjigae", "Jjimjilbang (Korean spa)"],
      rainyDay: ["Underground shopping", "Cafe hopping", "K-beauty shopping"],
      mildDay: ["Palace walks", "Hiking", "Hanok village stroll"]
    },
    avoid: []
  },
  bangkok: {
    id: "bangkok",
    name: "Bangkok",
    geography: {
      terrain: ["river_valley", "tropical"],
      nearOcean: false,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and humid",
      typicalWinter: "Hot and dry (cool season)",
      rainyPeriod: "May to October"
    },
    context: {
      knownFor: ["Temples", "street food", "nightlife", "markets"],
      commonActivities: ["temple visits", "street food", "shopping", "river cruises"],
      localLandmarks: ["Grand Palace", "Wat Arun", "Chatuchak Market"],
      culturalNotes: ["Land of Smiles", "Buddhist culture", "Street food paradise"]
    },
    suggestions: {
      hotDay: ["Standard Bangkok!", "Mall aircon", "Evening street food"],
      coldDay: ["Bangkok doesn't do cold", "Cool season is pleasant"],
      rainyDay: ["Afternoon monsoon storms", "Mall time", "Indoor markets"],
      mildDay: ["Temple touring", "River cruise", "Walking tours"]
    },
    avoid: ["cold weather references", "winter activities"]
  },
  bali: {
    id: "bali",
    name: "Bali",
    geography: {
      terrain: ["coastal", "tropical", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and dry (dry season)",
      typicalWinter: "Hot and wet (wet season)",
      rainyPeriod: "November to March"
    },
    context: {
      knownFor: ["Temples", "rice terraces", "surfing", "yoga"],
      commonActivities: ["surfing", "temple visits", "yoga", "rice terrace walks"],
      localLandmarks: ["Tanah Lot", "Tegallalang Rice Terraces", "Uluwatu"],
      culturalNotes: ["Hindu island", "Spiritual culture", "Tourist hub"]
    },
    suggestions: {
      hotDay: ["Beach day", "Pool time", "Surf session"],
      coldDay: ["Bali doesn't do cold", "Maybe cooler in Ubud hills"],
      rainyDay: ["Wet season storms", "Spa day", "Cooking class"],
      mildDay: ["Rice terrace walk", "Temple tour", "Sunrise at Batur"]
    },
    avoid: ["cold weather references", "winter activities"]
  },
  kuala_lumpur: {
    id: "kuala_lumpur",
    name: "Kuala Lumpur",
    geography: {
      terrain: ["tropical", "river_valley"],
      nearOcean: false,
      nearMountains: true,
      elevation: "low"
    },
    climate: {
      type: "tropical",
      seasons: "mild_year_round",
      typicalSummer: "Hot and humid year-round",
      typicalWinter: "Hot and humid year-round",
      rainyPeriod: "March to May, September to November"
    },
    context: {
      knownFor: ["Petronas Towers", "food", "shopping", "multicultural"],
      commonActivities: ["food tours", "shopping", "Batu Caves", "tower visits"],
      localLandmarks: ["Petronas Towers", "Batu Caves", "Central Market"],
      culturalNotes: ["Multicultural food paradise", "Malay, Chinese, Indian cultures"]
    },
    suggestions: {
      hotDay: ["Standard KL!", "Mall aircon (they love malls)", "Evening street food"],
      coldDay: ["KL doesn't do cold", "Genting Highlands for cool air"],
      rainyDay: ["Afternoon thunderstorm", "Mall hopping", "Food court feast"],
      mildDay: ["Batu Caves early", "KLCC Park", "Street food tour"]
    },
    avoid: ["cold weather references", "winter activities"]
  },
  // === EUROPE ===
  london: {
    id: "london",
    name: "London",
    geography: {
      terrain: ["river_valley"],
      nearOcean: false,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild to warm",
      typicalWinter: "Cool and grey"
    },
    context: {
      knownFor: ["Rain", "museums", "pubs", "royal history"],
      commonActivities: ["museum visits", "pub culture", "walking tours", "theatre"],
      localLandmarks: ["Big Ben", "Tower Bridge", "British Museum", "Buckingham Palace"],
      culturalNotes: ["The weather is always a topic", "Pub culture", "Queue politely"]
    },
    suggestions: {
      hotDay: ["Rare London heatwave!", "Parks", "Pub garden"],
      coldDay: ["Classic London", "Cosy pub", "Museum day"],
      rainyDay: ["Very London", "Museum hopping", "Pub time", "Theatre matinee"],
      mildDay: ["Perfect walking weather", "Parks", "Thames walk"]
    },
    avoid: ["beach activities"]
  },
  paris: {
    id: "paris",
    name: "Paris",
    geography: {
      terrain: ["river_valley", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm",
      typicalWinter: "Cool and grey"
    },
    context: {
      knownFor: ["Romance", "art", "food", "fashion"],
      commonActivities: ["museum visits", "cafe culture", "walking", "shopping"],
      localLandmarks: ["Eiffel Tower", "Louvre", "Notre-Dame", "Champs-\xC9lys\xE9es"],
      culturalNotes: ["Cafe culture", "August is quiet", "Fashion capital"]
    },
    suggestions: {
      hotDay: ["Rare Paris heat", "Museum aircon", "Seine-side"],
      coldDay: ["Cosy bistro", "Museum day", "Hot chocolate weather"],
      rainyDay: ["Very Parisian", "Louvre day", "Cafe hopping"],
      mildDay: ["Perfect strolling weather", "Luxembourg Gardens", "Montmartre walk"]
    },
    avoid: ["beach activities"]
  },
  amsterdam: {
    id: "amsterdam",
    name: "Amsterdam",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Mild to warm",
      typicalWinter: "Cool and wet"
    },
    context: {
      knownFor: ["Canals", "bikes", "museums", "tulips"],
      commonActivities: ["cycling", "canal cruises", "museum visits", "cafe culture"],
      localLandmarks: ["Anne Frank House", "Van Gogh Museum", "Rijksmuseum", "Canals"],
      culturalNotes: ["Everyone cycles", "Relaxed culture", "Tulip season in spring"]
    },
    suggestions: {
      hotDay: ["Canal-side drinks", "Park sunbathing", "Bike ride with breeze"],
      coldDay: ["Cosy brown cafe", "Museum day", "Warm stroopwafel"],
      rainyDay: ["Very Amsterdam", "Museum hopping", "Cafe culture"],
      mildDay: ["Perfect cycling weather", "Canal walk", "Vondelpark"]
    },
    avoid: ["beach activities"]
  },
  berlin: {
    id: "berlin",
    name: "Berlin",
    geography: {
      terrain: ["plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "continental",
      seasons: "four_seasons",
      typicalSummer: "Warm",
      typicalWinter: "Cold"
    },
    context: {
      knownFor: ["History", "nightlife", "art", "culture"],
      commonActivities: ["history tours", "gallery hopping", "nightlife", "park life"],
      localLandmarks: ["Brandenburg Gate", "Berlin Wall remains", "Reichstag", "Museum Island"],
      culturalNotes: ["Rich history", "Underground culture", "Techno capital"]
    },
    suggestions: {
      hotDay: ["Lakes around Berlin", "Park sunbathing", "Beer garden"],
      coldDay: ["Berlin winter!", "Museum Island", "Cosy bar"],
      rainyDay: ["Gallery day", "Museum Island", "Indoor markets"],
      mildDay: ["Walking tour weather", "Park life", "Spree riverside"]
    },
    avoid: ["beach activities"]
  },
  rome: {
    id: "rome",
    name: "Rome",
    geography: {
      terrain: ["plains", "river_valley"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "mediterranean",
      seasons: "four_seasons",
      typicalSummer: "Hot and dry",
      typicalWinter: "Mild and wet"
    },
    context: {
      knownFor: ["History", "food", "art", "Vatican"],
      commonActivities: ["ruins exploring", "food tours", "Vatican visits", "piazza life"],
      localLandmarks: ["Colosseum", "Vatican", "Trevi Fountain", "Pantheon"],
      culturalNotes: ["La dolce vita", "Pasta and gelato", "Afternoon riposo"]
    },
    suggestions: {
      hotDay: ["Siesta time", "Gelato essential", "Evening passeggiata"],
      coldDay: ["Rome cold is mild", "Museum day", "Cosy trattoria"],
      rainyDay: ["Vatican Museums", "Church hopping", "Long lunch"],
      mildDay: ["Perfect for ruins", "Walking tours", "Piazza sitting"]
    },
    avoid: ["beach activities"]
  },
  barcelona: {
    id: "barcelona",
    name: "Barcelona",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "four_seasons",
      typicalSummer: "Hot and humid",
      typicalWinter: "Mild"
    },
    context: {
      knownFor: ["Gaud\xED", "beaches", "food", "nightlife"],
      commonActivities: ["beach going", "Gaud\xED tours", "tapas", "La Rambla strolling"],
      localLandmarks: ["Sagrada Familia", "Park G\xFCell", "La Rambla", "Barceloneta Beach"],
      culturalNotes: ["Catalan culture", "Late dining", "Siesta tradition"]
    },
    suggestions: {
      hotDay: ["Beach day", "Siesta then tapas", "Evening stroll"],
      coldDay: ["Barcelona cold is mild", "Museum day", "Cosy tapas bar"],
      rainyDay: ["Sagrada Familia interior", "Market browsing", "Long lunch"],
      mildDay: ["Park G\xFCell", "Gothic Quarter walk", "Beach stroll"]
    },
    avoid: []
  },
  // === AMERICAS ===
  new_york: {
    id: "new_york",
    name: "New York",
    geography: {
      terrain: ["coastal", "river_valley"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "continental",
      seasons: "four_seasons",
      typicalSummer: "Hot and humid",
      typicalWinter: "Cold with snow"
    },
    context: {
      knownFor: ["Skyline", "Broadway", "pizza", "hustle"],
      commonActivities: ["Broadway shows", "Central Park", "museum visits", "food"],
      localLandmarks: ["Statue of Liberty", "Empire State Building", "Central Park", "Times Square"],
      culturalNotes: ["City that never sleeps", "Pizza and bagels", "Fast-paced"]
    },
    suggestions: {
      hotDay: ["Central Park shade", "Museum aircon", "Rooftop bar"],
      coldDay: ["Bundle up!", "Museum hopping", "Broadway matinee"],
      rainyDay: ["Museum day", "Broadway", "Shopping"],
      mildDay: ["Central Park", "Walking tours", "Outdoor dining"]
    },
    avoid: []
  },
  los_angeles: {
    id: "los_angeles",
    name: "Los Angeles",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "mild_year_round",
      typicalSummer: "Warm and dry",
      typicalWinter: "Mild (but rain possible)"
    },
    context: {
      knownFor: ["Hollywood", "beaches", "sunshine", "cars"],
      commonActivities: ["beach going", "hiking", "studio tours", "celebrity spotting"],
      localLandmarks: ["Hollywood Sign", "Santa Monica Pier", "Griffith Observatory"],
      culturalNotes: ["Car culture", "Health conscious", "Entertainment industry"]
    },
    suggestions: {
      hotDay: ["Beach day", "Pool time", "Evening activities"],
      coldDay: ["LA cold? Still nice!", "Museum day", "Cosy brunch"],
      rainyDay: ["Rare LA rain!", "Museum or studio tour", "Mall time"],
      mildDay: ["Classic LA weather", "Hiking", "Beach walk"]
    },
    avoid: []
  },
  san_francisco: {
    id: "san_francisco",
    name: "San Francisco",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: false,
      elevation: "sea_level"
    },
    climate: {
      type: "mediterranean",
      seasons: "mild_year_round",
      typicalSummer: "Cool and foggy (fogust!)",
      typicalWinter: "Mild and wet"
    },
    context: {
      knownFor: ["Golden Gate", "tech", "fog", "hills"],
      commonActivities: ["bridge walks", "cable cars", "Alcatraz", "food scene"],
      localLandmarks: ["Golden Gate Bridge", "Alcatraz", "Fisherman's Wharf", "Cable Cars"],
      culturalNotes: ["Layer your clothing!", "Tech hub", "Progressive culture"]
    },
    suggestions: {
      hotDay: ["Rare SF heat!", "Beach or park", "Rooftop bar"],
      coldDay: ["Summer in SF can feel cold!", "Layers essential", "Cosy cafe"],
      rainyDay: ["Museum day", "Ferry Building food hall", "Cafe hopping"],
      mildDay: ["Bridge walk", "Cable car ride", "Dolores Park"]
    },
    avoid: []
  },
  vancouver: {
    id: "vancouver",
    name: "Vancouver",
    geography: {
      terrain: ["coastal", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "oceanic",
      seasons: "four_seasons",
      typicalSummer: "Warm and dry",
      typicalWinter: "Mild and very wet"
    },
    context: {
      knownFor: ["Mountains", "ocean", "outdoor lifestyle", "multicultural"],
      commonActivities: ["hiking", "skiing", "beach walks", "Stanley Park"],
      localLandmarks: ["Stanley Park", "Grouse Mountain", "Granville Island"],
      culturalNotes: ["Outdoors obsessed", "Rain capital of Canada", "Asian influence"]
    },
    suggestions: {
      hotDay: ["Beach at English Bay", "Seawall walk", "Mountain escape"],
      coldDay: ["Ski day!", "Cosy cafe", "Indoor markets"],
      rainyDay: ["Classic Vancouver", "Museum or Granville Island", "Dim sum"],
      mildDay: ["Stanley Park", "Seawall", "Grouse Grind"]
    },
    avoid: []
  },
  toronto: {
    id: "toronto",
    name: "Toronto",
    geography: {
      terrain: ["coastal", "plains"],
      nearOcean: false,
      nearMountains: false,
      elevation: "low"
    },
    climate: {
      type: "continental",
      seasons: "four_seasons",
      typicalSummer: "Warm and humid",
      typicalWinter: "Very cold with snow"
    },
    context: {
      knownFor: ["CN Tower", "multicultural", "Raptors", "food scene"],
      commonActivities: ["CN Tower", "Distillery District", "food tours", "islands"],
      localLandmarks: ["CN Tower", "Toronto Islands", "Distillery District", "ROM"],
      culturalNotes: ["Most multicultural city", "Hockey culture", "Polite Canadians"]
    },
    suggestions: {
      hotDay: ["Toronto Islands", "Patio weather", "Lake Ontario"],
      coldDay: ["Toronto winter!", "PATH underground", "Hockey game"],
      rainyDay: ["ROM museum", "Underground PATH system", "Food markets"],
      mildDay: ["Distillery District", "Kensington Market", "Harbour walk"]
    },
    avoid: ["beach swimming in winter"]
  },
  // === PACIFIC ===
  fiji: {
    id: "fiji",
    name: "Fiji",
    geography: {
      terrain: ["coastal", "tropical", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "wet_dry",
      typicalSummer: "Hot and wet (wet season)",
      typicalWinter: "Warm and dry (dry season)",
      rainyPeriod: "November to April"
    },
    context: {
      knownFor: ["Paradise beaches", "friendly locals", "coral reefs", "bula!"],
      commonActivities: ["snorkeling", "diving", "beach lounging", "village visits"],
      localLandmarks: ["Coral reefs", "Mamanuca Islands", "Sabeto Hot Springs"],
      culturalNotes: ["Bula greeting", "Fiji time (relaxed)", "Kava ceremonies"]
    },
    suggestions: {
      hotDay: ["Standard Fiji!", "Ocean swim", "Shaded hammock"],
      coldDay: ["Fiji doesn't do cold", "Dry season is pleasant"],
      rainyDay: ["Tropical shower", "Spa time", "Kava ceremony"],
      mildDay: ["Snorkeling", "Island hopping", "Beach day"]
    },
    avoid: ["cold weather references", "winter activities"]
  },
  honolulu: {
    id: "honolulu",
    name: "Honolulu",
    geography: {
      terrain: ["coastal", "tropical", "mountainous"],
      nearOcean: true,
      nearMountains: true,
      elevation: "sea_level"
    },
    climate: {
      type: "tropical",
      seasons: "mild_year_round",
      typicalSummer: "Warm with trade winds",
      typicalWinter: "Warm with occasional rain"
    },
    context: {
      knownFor: ["Waikiki", "surfing", "Pearl Harbor", "aloha spirit"],
      commonActivities: ["surfing", "beach going", "hiking", "snorkeling"],
      localLandmarks: ["Waikiki Beach", "Diamond Head", "Pearl Harbor", "North Shore"],
      culturalNotes: ["Aloha spirit", "Surf culture", "Island time"]
    },
    suggestions: {
      hotDay: ["Beach day", "Shave ice", "Evening luau"],
      coldDay: ["Hawaii doesn't do cold", "Maybe a light jacket at night"],
      rainyDay: ["Quick tropical shower", "Museum or mall", "Wait it out"],
      mildDay: ["Perfect Hawaii weather", "Diamond Head hike", "Snorkeling"]
    },
    avoid: ["cold weather references", "winter activities"]
  }
};
function getLocationProfile(locationId) {
  const normalizedId = locationId.toLowerCase().replace(/[^a-z0-9]/g, "_");
  return LOCATION_PROFILES[normalizedId] || null;
}
function getLocationContextForAI(locationId) {
  const profile = getLocationProfile(locationId);
  if (!profile) {
    return "No specific location profile available.";
  }
  const lines = [
    `Location: ${profile.name}`,
    `Geography: ${profile.geography.terrain.join(", ")}${profile.geography.nearOcean ? " (coastal)" : " (inland)"}`,
    `Climate: ${profile.climate.type}, ${profile.climate.typicalSummer} summers, ${profile.climate.typicalWinter} winters`
  ];
  if (profile.context?.knownFor) {
    lines.push(`Known for: ${profile.context.knownFor.join(", ")}`);
  }
  if (profile.avoid && profile.avoid.length > 0) {
    lines.push(`AVOID suggesting: ${profile.avoid.join(", ")}`);
  }
  return lines.join("\n");
}
function getWeatherSuggestions(locationId, weatherCategory) {
  const profile = getLocationProfile(locationId);
  if (!profile) {
    return [];
  }
  const keyMap = { hot: "hotDay", cold: "coldDay", rainy: "rainyDay", mild: "mildDay" };
  return profile.suggestions[keyMap[weatherCategory]] || [];
}
function getAvoidList(locationId) {
  const profile = getLocationProfile(locationId);
  return profile?.avoid || [];
}
function getAllLocationProfiles() {
  return Object.values(LOCATION_PROFILES);
}
function getAllProfileIds() {
  return Object.keys(LOCATION_PROFILES);
}
function getProfilesByFilter(filter) {
  return Object.values(LOCATION_PROFILES).filter((profile) => {
    if (filter.terrain && !profile.geography.terrain.includes(filter.terrain)) {
      return false;
    }
    if (filter.climate && profile.climate.type !== filter.climate) {
      return false;
    }
    if (filter.nearOcean !== void 0 && profile.geography.nearOcean !== filter.nearOcean) {
      return false;
    }
    return true;
  });
}

// src/featureFlags.ts
var FEATURE_FLAGS = {
  air_quality_widget: {
    name: "Air Quality Widget",
    description: "AQI display with pollutant levels, UV index, and hourly trend chart",
    platforms: { web: true, mobile: true, api: true }
  },
  location_selector: {
    name: "Location Selector",
    description: "Searchable location picker with favorites, recents, and geolocation",
    platforms: { web: true, mobile: true, api: true }
  },
  humor_mode_inline: {
    name: "Inline Humor Mode Toggle",
    description: "Humor mode toggle directly on the stories feed page",
    platforms: { web: true, mobile: true, api: true }
  },
  generate_stories: {
    name: "Generate Stories",
    description: "Manual AI story generation and weather fetch controls",
    platforms: { web: true, mobile: false, api: true }
  },
  cloud_alternatives: {
    name: "Cloud ID Alternatives",
    description: "Show multiple cloud type predictions with confidence percentages",
    platforms: { web: true, mobile: false, api: true }
  },
  share_counts: {
    name: "Share Counts",
    description: "Display share count on story cards",
    platforms: { web: true, mobile: false, api: true }
  },
  fun_facts: {
    name: "Fun Weather Facts",
    description: "Random fun weather fact on the home page",
    platforms: { web: true, mobile: true, api: true }
  },
  rewards: {
    name: "Rewards System",
    description: "Points earning and reward redemption",
    platforms: { web: false, mobile: true, api: true }
  },
  story_detail: {
    name: "Story Detail Screen",
    description: "Dedicated full-screen story view with data context",
    platforms: { web: false, mobile: true, api: true }
  },
  push_notifications: {
    name: "Push Notifications",
    description: "Daily weather story push notifications",
    platforms: { web: false, mobile: true, api: true }
  }
};
function isFeatureEnabled(name, platform) {
  const flag = FEATURE_FLAGS[name];
  if (!flag) return false;
  return flag.platforms[platform];
}
function getFeatureParity() {
  return Object.values(FEATURE_FLAGS).map((flag) => ({
    feature: flag.name,
    description: flag.description,
    web: flag.platforms.web,
    mobile: flag.platforms.mobile,
    api: flag.platforms.api
  }));
}

// src/geo.ts
var GEOHASH_CHARS = "0123456789bcdefghjkmnpqrstuvwxyz";
function encodeGeohash(latitude, longitude, precision = 6) {
  let latRange = { min: -90, max: 90 };
  let lonRange = { min: -180, max: 180 };
  let hash = "";
  let bit = 0;
  let ch = 0;
  let isLon = true;
  while (hash.length < precision) {
    if (isLon) {
      const mid = (lonRange.min + lonRange.max) / 2;
      if (longitude >= mid) {
        ch |= 1 << 4 - bit;
        lonRange.min = mid;
      } else {
        lonRange.max = mid;
      }
    } else {
      const mid = (latRange.min + latRange.max) / 2;
      if (latitude >= mid) {
        ch |= 1 << 4 - bit;
        latRange.min = mid;
      } else {
        latRange.max = mid;
      }
    }
    isLon = !isLon;
    bit++;
    if (bit === 5) {
      hash += GEOHASH_CHARS[ch];
      bit = 0;
      ch = 0;
    }
  }
  return hash;
}
var STATE_TIMEZONE_MAP = {
  WA: "Australia/Perth",
  SA: "Australia/Adelaide",
  VIC: "Australia/Melbourne",
  NSW: "Australia/Sydney",
  QLD: "Australia/Brisbane",
  TAS: "Australia/Hobart",
  NT: "Australia/Darwin",
  ACT: "Australia/Sydney"
};
function getTimezoneForLocation(state, latitude, longitude) {
  if (longitude < 106) return "Indian/Christmas";
  if (longitude < 97) return "Indian/Cocos";
  if (longitude > 158 && latitude > -32) return "Pacific/Norfolk";
  if (longitude > 158 && latitude < -50) return "Australia/Hobart";
  if (longitude > 158 && latitude > -32.5 && latitude < -31) return "Australia/Lord_Howe";
  return STATE_TIMEZONE_MAP[state] || "Australia/Sydney";
}
function toTitleCase(str) {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()).replace(/\bMc(\w)/g, (_match, letter) => `Mc${letter.toUpperCase()}`).replace(/\bO'(\w)/g, (_match, letter) => `O'${letter.toUpperCase()}`);
}
function deriveCityFromStationName(name) {
  let city = name.replace(/\s*\(.*?\)\s*/g, " ").trim();
  const suffixes = [
    /\s+airport$/i,
    /\s+aero$/i,
    /\s+aerodrome$/i,
    /\s+aws$/i,
    /\s+amo$/i,
    /\s+post\s+office$/i,
    /\s+comparison$/i,
    /\s+racecourse$/i,
    /\s+lighthouse$/i,
    /\s+radar$/i,
    /\s+composite$/i,
    /\s+defence$/i,
    /\s+raaf$/i,
    /\s+naval$/i,
    /\s+station$/i,
    /\s+observatory$/i,
    /\s+met\s+office$/i,
    /\s+weather\s+station$/i
  ];
  for (const suffix of suffixes) {
    city = city.replace(suffix, "");
  }
  return city.trim();
}

// src/astronomical.ts
var SYNODIC_MONTH = 29.53059;
var MOON_PHASES = [
  { name: "new_moon", label: "New Moon", emoji: "\u{1F311}", range: [0, 0.0625] },
  { name: "waxing_crescent", label: "Waxing Crescent", emoji: "\u{1F312}", range: [0.0625, 0.1875] },
  { name: "first_quarter", label: "First Quarter", emoji: "\u{1F313}", range: [0.1875, 0.3125] },
  { name: "waxing_gibbous", label: "Waxing Gibbous", emoji: "\u{1F314}", range: [0.3125, 0.4375] },
  { name: "full_moon", label: "Full Moon", emoji: "\u{1F315}", range: [0.4375, 0.5625] },
  { name: "waning_gibbous", label: "Waning Gibbous", emoji: "\u{1F316}", range: [0.5625, 0.6875] },
  { name: "last_quarter", label: "Last Quarter", emoji: "\u{1F317}", range: [0.6875, 0.8125] },
  { name: "waning_crescent", label: "Waning Crescent", emoji: "\u{1F318}", range: [0.8125, 1] }
];
function getMoonPhase(date) {
  const knownNewMoon = new Date(2e3, 0, 6, 18, 14, 0);
  const daysSinceNewMoon = (date.getTime() - knownNewMoon.getTime()) / (1e3 * 60 * 60 * 24);
  const cyclePosition = (daysSinceNewMoon % SYNODIC_MONTH + SYNODIC_MONTH) % SYNODIC_MONTH;
  const phase = cyclePosition / SYNODIC_MONTH;
  const currentPhase = MOON_PHASES.find((p) => phase >= p.range[0] && phase < p.range[1]) || MOON_PHASES[0];
  const illumination = Math.round((1 - Math.cos(phase * 2 * Math.PI)) / 2 * 100);
  const daysUntilFull = phase < 0.5 ? Math.round((0.5 - phase) * SYNODIC_MONTH) : Math.round((1.5 - phase) * SYNODIC_MONTH);
  const daysUntilNew = Math.round((1 - phase) * SYNODIC_MONTH);
  return {
    phase: currentPhase.name,
    phaseName: currentPhase.label,
    illumination,
    daysUntilFull,
    daysUntilNew,
    emoji: currentPhase.emoji
  };
}
function getSolsticeEquinoxInfo(date, hemisphere = "southern") {
  const year = date.getFullYear();
  const events = [
    {
      name: "March Equinox",
      date: new Date(year, 2, 20),
      southern: { type: "equinox", desc: "Autumn Equinox - Day equals night" },
      northern: { type: "equinox", desc: "Spring Equinox - Day equals night" }
    },
    {
      name: "June Solstice",
      date: new Date(year, 5, 21),
      southern: { type: "solstice", desc: "Winter Solstice - Shortest day of the year" },
      northern: { type: "solstice", desc: "Summer Solstice - Longest day of the year" }
    },
    {
      name: "September Equinox",
      date: new Date(year, 8, 23),
      southern: { type: "equinox", desc: "Spring Equinox - Day equals night" },
      northern: { type: "equinox", desc: "Autumn Equinox - Day equals night" }
    },
    {
      name: "December Solstice",
      date: new Date(year, 11, 21),
      southern: { type: "solstice", desc: "Summer Solstice - Longest day of the year" },
      northern: { type: "solstice", desc: "Winter Solstice - Shortest day of the year" }
    }
  ];
  events.push({
    name: "March Equinox",
    date: new Date(year + 1, 2, 20),
    southern: { type: "equinox", desc: "Autumn Equinox - Day equals night" },
    northern: { type: "equinox", desc: "Spring Equinox - Day equals night" }
  });
  const upcoming = events.map((e) => ({
    name: e.name,
    date: e.date,
    type: e[hemisphere].type,
    description: e[hemisphere].desc,
    daysUntil: Math.ceil((e.date.getTime() - date.getTime()) / (1e3 * 60 * 60 * 24))
  })).filter((e) => e.daysUntil > 0).sort((a, b) => a.daysUntil - b.daysUntil)[0];
  return {
    name: upcoming.name,
    type: upcoming.type,
    date: upcoming.date,
    daysUntil: upcoming.daysUntil,
    description: upcoming.description,
    hemisphere
  };
}
function getSeason(date, hemisphere = "southern") {
  const month = date.getMonth();
  const seasons = hemisphere === "southern" ? ["summer", "summer", "autumn", "autumn", "autumn", "winter", "winter", "winter", "spring", "spring", "spring", "summer"] : ["winter", "winter", "spring", "spring", "spring", "summer", "summer", "summer", "autumn", "autumn", "autumn", "winter"];
  return seasons[month];
}
function getClientAstronomicalData(date, hemisphere) {
  return {
    moon: getMoonPhase(date),
    nextEvent: getSolsticeEquinoxInfo(date, hemisphere),
    season: getSeason(date, hemisphere),
    dayOfYear: getDayOfYear(date),
    isLeapYear: isLeapYear(date.getFullYear())
  };
}
export {
  ACCESS_TOKEN_EXPIRY,
  ALLOWED_IMAGE_TYPES,
  ALL_CITIES,
  API_VERSION,
  AUSTRALIAN_MAJOR_CITIES,
  AUSTRALIAN_REGIONAL_CITIES,
  CLIMATE_SCENARIOS,
  DAILY_STORY_COUNT,
  DAY_NAMES,
  DEFAULT_PAGE_SIZE,
  ERROR_CODES,
  FEATURE_FLAGS,
  FORECAST_CACHE_TTL,
  FUN_REFERENCES,
  HISTORICAL_YEARS_DEFAULT,
  HUMOR_PROMPTS,
  IMAGE_COMPRESSION_QUALITY,
  INTERNATIONAL_CITIES,
  INTERNATIONAL_PROFILES,
  LEVEL_THRESHOLDS,
  LOCATION_PROFILES,
  MAX_IMAGE_SIZE,
  MAX_PAGE_SIZE,
  MAX_STORY_LENGTH,
  MONTH_FACTS,
  MONTH_NAMES,
  MONTH_NAMES_SHORT,
  PASSWORD_MIN_LENGTH,
  PERCENTILE_NOTABLE_HIGH,
  PERCENTILE_NOTABLE_LOW,
  POINTS_DAILY_LOGIN,
  POINTS_REFERRAL,
  POINTS_SHARE_STORY,
  POINTS_UPLOAD_CLOUD,
  PRICE_BASIC_MONTHLY,
  PRICE_BASIC_YEARLY,
  PROJECTION_PERIODS,
  RATE_LIMIT_API_BASIC,
  RATE_LIMIT_AUTHENTICATED,
  RATE_LIMIT_PREMIUM,
  RATE_LIMIT_PUBLIC,
  REFRESH_TOKEN_EXPIRY,
  SEASONS_SOUTHERN,
  STATE_TIMEZONE_MAP,
  STORY_CACHE_TTL,
  STREAK_MIN_DAYS,
  WEATHER_CACHE_TTL,
  WEATHER_TRIVIA,
  Z_SCORE_EXTREME,
  Z_SCORE_NOTABLE,
  addDays,
  calculateStreakDays,
  celsiusToFahrenheit,
  daysInYear,
  deriveCityFromStationName,
  differenceInDays,
  encodeGeohash,
  endOfDay,
  fahrenheitToCelsius,
  formatCloudType,
  formatDate,
  formatDateInTimezone,
  formatHumidity,
  formatNumber,
  formatOrdinal,
  formatPercentage,
  formatPrecipitation,
  formatRelativeTime,
  formatStoryType,
  formatTemperature,
  formatTemperatureChange,
  formatTemperatureRange,
  formatUVIndex,
  formatWeatherCondition,
  formatWindSpeed,
  getAllLocationProfiles,
  getAllProfileIds,
  getAvoidList,
  getClientAstronomicalData,
  getComparative,
  getCurrentDateInTimezone,
  getDayOfYear,
  getFeatureParity,
  getLocationContextForAI,
  getLocationProfile,
  getMoonPhase,
  getNorthernHemisphereSeason,
  getOnThisDayDate,
  getOnThisDayDates,
  getPasswordStrength,
  getProfilesByFilter,
  getSeason,
  getSolsticeEquinoxInfo,
  getSouthernHemisphereSeason,
  getSuperlative,
  getTimezoneForLocation,
  getWeatherSuggestions,
  isFeatureEnabled,
  isLeapYear,
  isToday,
  isTodayInTimezone,
  isValidApiKey,
  isValidBomStationId,
  isValidCoordinates,
  isValidDateString,
  isValidDisplayName,
  isValidEmail,
  isValidHumidity,
  isValidLatitude,
  isValidLongitude,
  isValidPassword,
  isValidPrecipitation,
  isValidTemperature,
  isValidUVIndex,
  isValidWindSpeed,
  parseISO,
  sanitizeInput,
  startOfDay,
  subDays,
  toTitleCase
};
